export interface ISourcingWorkHistoryItem {
  readonly company: string;
  readonly logoUrl: string | null;
  readonly title: string;
  readonly duration: string;
}

export interface ISourcingPerson {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  readonly tenure?: string | null;
  readonly headline?: string | null;
  readonly education?: string | null;
  readonly location: string;
  readonly avatarUrl: string | null;
  readonly linkedinUrl: string | null;
  readonly twitterUrl: string | null;
  readonly facebookUrl: string | null;
  readonly recentWorkHistory: readonly ISourcingWorkHistoryItem[];
}

export interface ISourcingFundingRound {
  readonly stage: string;
  readonly round: string;
  readonly date: string;
  readonly raised: number;
  readonly postMoney: number | null;
  readonly leadInvestors: string[];
  readonly allInvestors: string[];
  /**
   * Origin of the underlying deal: 'CB' = Crunchbase, 'PB' = PitchBook.
   * Optional so existing mocks (which don't track provenance) keep
   * compiling. The adapter sets this from the backend `source` field
   * and uses it to dedup overlapping rounds (CB > PB).
   */
  readonly source?: 'CB' | 'PB';
}

export interface ISourcingTrafficStat {
  readonly label: string;
  readonly value: string;
  readonly delta: string;
  readonly deltaPositive: boolean;
  readonly deltaDescription: string;
}

export interface ISourcingProfileData {
  readonly id: string;
  readonly name: string;
  readonly logoUrl: string | null;
  readonly website: string | null;
  readonly description: string;
  readonly tags: string[];
  readonly enrichedDate: string;
  readonly listsCount?: number;

  // Header stats
  readonly trafficStats: readonly ISourcingTrafficStat[];

  // Operational status (e.g. 'Operating', 'Acquired')
  readonly operationalStatus: string | null;

  // Overview sidebar fields
  readonly ceoName: string | null;
  readonly founders: string[];
  readonly foundedYear: number | null;
  readonly headcount: string | null;
  readonly totalRaised: number | null;
  readonly annualRevenue: number | null;
  readonly revenuePerEmployee: number | null;
  readonly hq: string | null;
  readonly sector: string[];
  readonly industry: string[];
  readonly keywords: string[];
  readonly universe: string[];
  readonly linkedinUrl: string | null;
  readonly twitterUrl: string | null;
  readonly facebookUrl: string | null;
  readonly crunchbaseUrl: string | null;
  readonly pitchbookUrl: string | null;
  readonly harmonicUrl: string | null;

  // Foresight summary
  readonly foresightSummary: readonly string[];

  // Growth Metrics chart — Headcount tab. One entry per trailing window
  // (14d / 30d / 90d / 180d / 365d or the legacy 1M / 3M / 6M / 12M labels
  // used in fixtures).
  readonly fundingEvolution: readonly {
    readonly stage: string;
    readonly headcount: number;
    readonly growthPct: number;
  }[];

  // Growth Metrics chart — Web Traffic tab. Same shape as
  // `fundingEvolution` but the `value` is renamed to `visits` because the
  // Y-axis units and tooltip copy differ. Optional because the mocks
  // haven't been extended yet — live responses populate it from
  // `traction.web_traffic.growth`.
  readonly webTrafficEvolution?: readonly {
    readonly stage: string;
    readonly visits: number;
    readonly growthPct: number;
  }[];

  // Financing history
  readonly fundingRounds: readonly ISourcingFundingRound[];

  // Highlights
  readonly highlights: readonly string[];

  // Company details
  readonly legalName: string | null;
  readonly companyType: string | null;
  readonly alsoKnownAs: string[];
  readonly secondaryHq: string | null;
  readonly acquiredBy: string | null;

  // Key people & founders
  readonly keyPeople: readonly ISourcingPerson[];
  readonly founderPeople: readonly ISourcingPerson[];

  // Contacts
  readonly email: string | null;
  readonly phone: string | null;

  // Similar companies (reuse funding round table structure)
  readonly similarCompanies: readonly ISourcingFundingRound[];
}

export const SOURCING_PROFILE_MOCK: ISourcingProfileData = {
  id: 'anthropic',
  name: 'Anthropic',
  logoUrl: null,
  website: 'anthropic.com',
  description:
    'Anthropic is an AI safety and research company that builds reliable, interpretable, and steerable AI systems.',
  tags: ['Active', 'Private', 'Startup', 'B2C', 'AI Infra', 'Artificial Intelligence (AI)'],
  enrichedDate: 'Mar 1, 2026',
  listsCount: 3,

  trafficStats: [
    {
      label: 'Monthly Web Traffic',
      value: '120.5K',
      delta: '+2.1%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '5.2K',
      delta: '+989',
      deltaPositive: true,
      deltaDescription: 'over the last 6M',
    },
    {
      label: 'X Followers',
      value: '12.1K',
      delta: '+120',
      deltaPositive: true,
      deltaDescription: 'over the last 6M',
    },
    {
      label: 'Headcount',
      value: '50-199',
      delta: '+5.3%',
      deltaPositive: true,
      deltaDescription: 'over the last 6M',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Tom Reeves',
  founders: ['Mark Daughters', '+2'],
  foundedYear: 2014,
  headcount: '50-199',
  totalRaised: 142_860_000,
  annualRevenue: 28_000_000,
  revenuePerEmployee: 81_800,
  hq: 'San Francisco, CA, USA',
  sector: ['Artificial Intelligence (AI)', 'AI Infra'],
  industry: ['Artificial Intelligence (AI)', 'AI Infra'],
  keywords: ['M&A', 'Venture Capital'],
  universe: ['SaaS'],
  linkedinUrl: 'https://linkedin.com/company/anthropic',
  twitterUrl: 'https://twitter.com/anthropic',
  facebookUrl: 'https://facebook.com/anthropic',
  crunchbaseUrl: 'https://crunchbase.com/organization/anthropic',
  pitchbookUrl: null,
  harmonicUrl: 'https://harmonic.ai/companies/anthropic',

  foresightSummary: [
    'Anthropic is a Series C company that has raised $142M across 6 rounds, with its latest funding in May 2024.',
    'The company reports $28M in annual revenue and is currently in a scaling phase, with headcount growing 12% over the past 6 months.',
    'Hiring activity is accelerating, supported by a 9.5% year-over-year increase in team size.',
    'With approximately $81.8K in revenue per employee, the company shows consistent growth and ongoing investment in its AI Infrastructure capabilities.',
  ],

  fundingEvolution: [
    { stage: '1M', headcount: 12, growthPct: 4 },
    { stage: '3M', headcount: 38, growthPct: 8 },
    { stage: '6M', headcount: 132, growthPct: 46 },
    { stage: '12M', headcount: 208, growthPct: 35 },
  ],

  fundingRounds: [
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Dec 18, 2025',
      raised: 7_500_000,
      postMoney: 822_000,
      leadInvestors: ['Frontline Ventures'],
      allInvestors: ['Frontline Ventures', 'Coatue', 'Thrive Capital', 'Benchmark', 'Tiger Global', '+5'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Nov 11, 2024',
      raised: 6_400_000,
      postMoney: 330_000,
      leadInvestors: ['Felicis'],
      allInvestors: ['Felicis', 'Spark Capital', 'GV', 'First Round', 'a16z', '+6'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'May 1, 2023',
      raised: 4_500_000,
      postMoney: 95_000,
      leadInvestors: [],
      allInvestors: ['Operator Partners', 'Sequoia', 'YC', 'Kleiner Perkins', 'Lightspeed', '+5'],
    },
    {
      stage: 'Seed',
      round: 'Seed',
      date: 'Jan 20, 2022',
      raised: 2_300_000,
      postMoney: 20_000,
      leadInvestors: [],
      allInvestors: ['SVA', 'Hustle Fund', 'Precursor', '+4'],
    },
  ],

  highlights: [
    'Raised $740M from Benchmark, Coatue, Thrive +12 investors',
    'Headcount up 40% in 6 months — actively hiring engineers',
    'ISO 27001 certification in progress',
  ],

  legalName: 'Anthropic PBC (formerly Anthropic AI)',
  alsoKnownAs: ['Anthropic', 'Anthropic AI', 'anthropic.ai', 'Claude AI', 'Anthropic AI Research'],
  secondaryHq: 'Los Angeles, CA, USA',
  companyType: 'Startup',
  acquiredBy: 'Apple',

  keyPeople: [
    {
      id: 'tom-reeves',
      name: 'Tom Reeves',
      title: 'Founder & CEO',
      tenure: '2.3 years',
      headline: 'Founder & CEO building reliable AI systems',
      education: 'Princeton University, Biophysics',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/tomreeves',
      twitterUrl: 'https://twitter.com/tomreeves',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Petcube', logoUrl: null, title: 'Co-Founder', duration: '1.1 years' },
        { company: 'Adidas', logoUrl: null, title: 'Co-Founder', duration: '0.8 years' },
        { company: 'Slack Inc.', logoUrl: null, title: 'CTO', duration: '1.3 years' },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'mark-daughters',
      name: 'Mark Daughters',
      title: 'Founder & SVP',
      tenure: 'Present',
      headline: 'Some longlonglong headline from LinkedIn',
      education: null,
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/markdaughters',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'USAA', logoUrl: null, title: 'Chief Architect', duration: 'Present' },
        { company: 'Adidas', logoUrl: null, title: 'Co-Founder', duration: '0.8 years' },
      ],
    },
    {
      id: 'josef-astanavista',
      name: 'Josef Astanavista',
      title: 'CRO',
      tenure: '1.5 years',
      headline: 'Some longlonglong headline from LinkedIn',
      education: null,
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: null,
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Coca-Cola', logoUrl: null, title: 'VP of Marketing', duration: '3.1 years' },
        { company: 'Adidas', logoUrl: null, title: 'VP of Marketing', duration: '1.1 years' },
        { company: 'Zapier', logoUrl: null, title: 'CTO', duration: '1.1 years' },
      ],
    },
    {
      id: 'sarah-onora',
      name: "Sarah O'Nora",
      title: 'VP of Engineering',
      tenure: '2 years',
      headline: 'Some longlonglong headline from LinkedIn',
      education: 'UCL (University College London)',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: null,
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Acorns', logoUrl: null, title: 'VP of Sales', duration: '1.8 years' },
        { company: 'Adidas', logoUrl: null, title: 'VP of Sales', duration: '1.1 years' },
        { company: 'HeyGen', logoUrl: null, title: 'CTO', duration: '1.1 years' },
      ],
    },
  ],

  email: 'support@anthropic.com',
  phone: '+1 373 777 0990',

  similarCompanies: [
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Dec 18, 2025',
      raised: 7_500_000,
      postMoney: 822_000,
      leadInvestors: ['Frontline Ventures'],
      allInvestors: ['Frontline Ventures', '+5'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Nov 11, 2024',
      raised: 6_400_000,
      postMoney: 330_000,
      leadInvestors: ['Felicis'],
      allInvestors: ['Felicis', '+6'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'May 1, 2023',
      raised: 4_500_000,
      postMoney: 95_000,
      leadInvestors: [],
      allInvestors: ['Operator Partners', '+5'],
    },
    {
      stage: 'Seed',
      round: 'Seed',
      date: 'Jan 20, 2022',
      raised: 2_300_000,
      postMoney: 20_000,
      leadInvestors: [],
      allInvestors: ['SVA', '+4'],
    },
  ],
};

const OPENAI_PROFILE = {
  id: 'openai',
  name: 'OpenAI',
  logoUrl: 'https://logo.clearbit.com/openai.com',
  website: 'openai.com',
  description:
    'OpenAI is an AI research and deployment company dedicated to ensuring artificial general intelligence benefits all of humanity. They build advanced AI models including GPT and DALL-E.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'B2C', 'AI Research', 'Artificial Intelligence (AI)'],
  enrichedDate: 'Mar 15, 2026',
  listsCount: 5,

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '1,840,200,000',
      delta: '+8.3%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '4,120,000',
      delta: '+185,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '3,900,000',
      delta: '+420,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '3,000-5,000',
      delta: '+480',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Sam Altman',
  founders: ['Sam Altman', 'Greg Brockman', '+3'],
  foundedYear: 2015,
  headcount: '3,000-5,000',
  totalRaised: 13_300_000_000,
  annualRevenue: 3_400_000_000,
  revenuePerEmployee: 850_000,
  hq: 'San Francisco, CA, USA',
  sector: ['Artificial Intelligence (AI)', 'AI Research'],
  industry: ['Artificial Intelligence (AI)', 'Machine Learning'],
  keywords: ['Generative AI', 'LLM', 'AGI', 'Venture Capital'],
  universe: ['SaaS', 'AI Platform'],
  linkedinUrl: 'https://linkedin.com/company/openai',
  twitterUrl: 'https://twitter.com/openai',
  facebookUrl: 'https://facebook.com/openai',
  crunchbaseUrl: 'https://crunchbase.com/organization/openai',
  pitchbookUrl: 'https://pitchbook.com/profiles/openai',
  harmonicUrl: null,

  foresightSummary: [
    'OpenAI is a late-stage company that has raised $13.3B across multiple rounds, with its latest funding in Oct 2024.',
    'The company reports an estimated $3.4B in annual revenue and is in hyper-growth mode, with headcount surging 18% over the past 6 months.',
    'Hiring activity continues to accelerate, with particular emphasis on research engineers and safety specialists.',
    'With approximately $850K in revenue per employee, OpenAI demonstrates exceptional capital efficiency for its scale and continues to dominate the generative AI market.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 60, growthPct: 0 },
    { stage: 'Series A', headcount: 200, growthPct: 45 },
    { stage: 'Series B', headcount: 750, growthPct: 120 },
    { stage: 'Series C', headcount: 2200, growthPct: 85 },
    { stage: 'Series D', headcount: 3800, growthPct: 55 },
  ],

  fundingRounds: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Oct 2, 2024',
      raised: 6_600_000_000,
      postMoney: 157_000_000_000,
      leadInvestors: ['Thrive Capital'],
      allInvestors: ['Thrive Capital', 'Microsoft', 'Khosla Ventures', 'Infosys', 'SoftBank', '+8'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Jan 10, 2023',
      raised: 10_000_000_000,
      postMoney: 86_000_000_000,
      leadInvestors: ['Microsoft'],
      allInvestors: ['Microsoft'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Jul 15, 2021',
      raised: 1_000_000_000,
      postMoney: 27_000_000_000,
      leadInvestors: ['Tiger Global'],
      allInvestors: ['Tiger Global', 'a16z', 'Sequoia', 'Bedrock Capital', '+4'],
    },
  ],

  highlights: [
    'Raised $6.6B in Series D at $157B valuation — largest private AI round',
    'ChatGPT reached 200M weekly active users globally',
    'Expanded enterprise offering with ChatGPT Enterprise and API platform',
    'Strategic partnership with Microsoft valued at $13B+',
  ],

  legalName: 'OpenAI OpCo, LLC',
  alsoKnownAs: ['OpenAI LP', 'OpenAI Inc'],
  secondaryHq: null,
  companyType: null,
  acquiredBy: 'Microsoft',

  keyPeople: [
    {
      id: 'sam-altman',
      name: 'Sam Altman',
      title: 'CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/samaltman',
      twitterUrl: 'https://twitter.com/sama',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Y Combinator',
          logoUrl: 'https://logo.clearbit.com/ycombinator.com',
          title: 'President',
          duration: '5y 2m',
        },
        { company: 'Loopt', logoUrl: null, title: 'CEO & Co-Founder', duration: '7y 4m' },
      ],
    },
    {
      id: 'greg-brockman',
      name: 'Greg Brockman',
      title: 'President',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/thegdb',
      twitterUrl: 'https://twitter.com/gaborbrockman',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Stripe',
          logoUrl: 'https://logo.clearbit.com/stripe.com',
          title: 'CTO',
          duration: '4y 1m',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'sam-altman-f',
      name: 'Sam Altman',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/samaltman',
      twitterUrl: 'https://twitter.com/sama',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Y Combinator',
          logoUrl: 'https://logo.clearbit.com/ycombinator.com',
          title: 'President',
          duration: '5y 2m',
        },
      ],
    },
    {
      id: 'greg-brockman-f',
      name: 'Greg Brockman',
      title: 'Co-Founder & President',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/thegdb',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Stripe',
          logoUrl: 'https://logo.clearbit.com/stripe.com',
          title: 'CTO',
          duration: '4y 1m',
        },
      ],
    },
  ],

  email: 'support@openai.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Oct 2, 2024',
      raised: 6_600_000_000,
      postMoney: 157_000_000_000,
      leadInvestors: ['Thrive Capital'],
      allInvestors: ['Thrive Capital', '+8'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Jan 10, 2023',
      raised: 10_000_000_000,
      postMoney: 86_000_000_000,
      leadInvestors: ['Microsoft'],
      allInvestors: ['Microsoft'],
    },
  ],
} as const satisfies ISourcingProfileData;

const STRIPE_PROFILE = {
  id: 'stripe',
  name: 'Stripe',
  logoUrl: 'https://logo.clearbit.com/stripe.com',
  website: 'stripe.com',
  description:
    'Stripe is a financial infrastructure platform for businesses, providing payment processing APIs and tools for internet commerce.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'Fintech', 'Payments'],
  enrichedDate: 'Mar 10, 2026',
  listsCount: 2,

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '89,340,000',
      delta: '+3.2%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '1,250,000',
      delta: '+42,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '980,000',
      delta: '+31,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '8,000-10,000',
      delta: '+320',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Patrick Collison',
  founders: ['Patrick Collison', 'John Collison'],
  foundedYear: 2010,
  headcount: '8,000-10,000',
  totalRaised: 8_700_000_000,
  annualRevenue: 14_000_000_000,
  revenuePerEmployee: 1_555_000,
  hq: 'San Francisco, CA, USA',
  sector: ['Fintech', 'Payments'],
  industry: ['Financial Services', 'Payment Processing'],
  keywords: ['Payments', 'Commerce', 'API', 'Venture Capital'],
  universe: ['SaaS', 'Fintech'],
  linkedinUrl: 'https://linkedin.com/company/stripe',
  twitterUrl: 'https://twitter.com/stripe',
  facebookUrl: 'https://facebook.com/StripeHQ',
  crunchbaseUrl: 'https://crunchbase.com/organization/stripe',
  pitchbookUrl: 'https://pitchbook.com/profiles/stripe',
  harmonicUrl: null,

  foresightSummary: [
    'Stripe is a late-stage private company that has raised $8.7B across multiple rounds, with its latest funding in Mar 2023.',
    'The company reports approximately $14B in annual revenue, making it one of the most valuable private fintech companies globally.',
    'Hiring has stabilized after a 2023 restructuring, with headcount growing modestly at 4.1% over the past 6 months.',
    'With approximately $1.55M in revenue per employee, Stripe demonstrates exceptional capital efficiency in the payments infrastructure space.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 10, growthPct: 0 },
    { stage: 'Series A', headcount: 80, growthPct: 60 },
    { stage: 'Series B', headcount: 400, growthPct: 90 },
    { stage: 'Series C', headcount: 1800, growthPct: 75 },
    { stage: 'Series E+', headcount: 9000, growthPct: 15 },
  ],

  fundingRounds: [
    {
      stage: 'Series I',
      round: 'Series I',
      date: 'Mar 15, 2023',
      raised: 6_500_000_000,
      postMoney: 50_000_000_000,
      leadInvestors: ['Sequoia', 'a16z'],
      allInvestors: ['Sequoia', 'a16z', 'GIC', 'Goldman Sachs', 'Founders Fund', '+7'],
    },
    {
      stage: 'Series H',
      round: 'Series H',
      date: 'Mar 14, 2021',
      raised: 600_000_000,
      postMoney: 95_000_000_000,
      leadInvestors: ['Sequoia'],
      allInvestors: ['Sequoia', 'Fidelity', 'Baillie Gifford', 'ABI', '+5'],
    },
    {
      stage: 'Series G',
      round: 'Series G',
      date: 'Apr 16, 2020',
      raised: 850_000_000,
      postMoney: 36_000_000_000,
      leadInvestors: ['General Catalyst', 'Sequoia'],
      allInvestors: ['General Catalyst', 'Sequoia', 'GV', 'a16z', '+6'],
    },
  ],

  highlights: [
    'Processing $1T+ in total payment volume annually',
    'IPO plans reportedly in advanced stages for 2025-2026',
    'Expanded to 46+ countries with local acquiring capabilities',
    'Launched Stripe Financial Connections and Revenue Recognition',
  ],

  legalName: 'Stripe, Inc.',
  alsoKnownAs: ['/dev/payments'],
  secondaryHq: 'Dublin, Ireland',
  companyType: null,
  acquiredBy: 'Sequoia Capital',

  keyPeople: [
    {
      id: 'patrick-collison',
      name: 'Patrick Collison',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/patrickcollison',
      twitterUrl: 'https://twitter.com/patrickc',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Auctomatic', logoUrl: null, title: 'Co-Founder & CEO', duration: '1y 6m' },
      ],
    },
    {
      id: 'john-collison',
      name: 'John Collison',
      title: 'Co-Founder & President',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/johncollison',
      twitterUrl: 'https://twitter.com/collision',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Auctomatic', logoUrl: null, title: 'Co-Founder', duration: '1y 6m' }],
    },
  ],

  founderPeople: [
    {
      id: 'patrick-collison-f',
      name: 'Patrick Collison',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/patrickcollison',
      twitterUrl: 'https://twitter.com/patrickc',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Auctomatic', logoUrl: null, title: 'Co-Founder & CEO', duration: '1y 6m' },
      ],
    },
    {
      id: 'john-collison-f',
      name: 'John Collison',
      title: 'Co-Founder & President',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/johncollison',
      twitterUrl: 'https://twitter.com/collision',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Auctomatic', logoUrl: null, title: 'Co-Founder', duration: '1y 6m' }],
    },
  ],

  email: 'sales@stripe.com',
  phone: '+1 888 926 2289',

  similarCompanies: [
    {
      stage: 'Series I',
      round: 'Series I',
      date: 'Mar 15, 2023',
      raised: 6_500_000_000,
      postMoney: 50_000_000_000,
      leadInvestors: ['Sequoia'],
      allInvestors: ['Sequoia', '+7'],
    },
    {
      stage: 'Series H',
      round: 'Series H',
      date: 'Mar 14, 2021',
      raised: 600_000_000,
      postMoney: 95_000_000_000,
      leadInvestors: ['Sequoia'],
      allInvestors: ['Sequoia', '+5'],
    },
  ],
} as const satisfies ISourcingProfileData;

const DATABRICKS_PROFILE = {
  id: 'databricks',
  name: 'Databricks',
  logoUrl: 'https://logo.clearbit.com/databricks.com',
  website: 'databricks.com',
  description:
    'Databricks is a unified data analytics platform combining data engineering, data science, and machine learning on a lakehouse architecture.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'Data Platform', 'AI/ML'],
  enrichedDate: 'Mar 12, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '18,920,000',
      delta: '+6.7%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '890,000',
      delta: '+62,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '310,000',
      delta: '+18,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '5,000-7,000',
      delta: '+540',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Ali Ghodsi',
  founders: ['Ali Ghodsi', 'Ion Stoica', '+5'],
  foundedYear: 2013,
  headcount: '5,000-7,000',
  totalRaised: 4_100_000_000,
  annualRevenue: 1_600_000_000,
  revenuePerEmployee: 266_600,
  hq: 'San Francisco, CA, USA',
  sector: ['Data Platform', 'AI/ML'],
  industry: ['Data Analytics', 'Cloud Computing'],
  keywords: ['Lakehouse', 'Apache Spark', 'Data Engineering', 'Venture Capital'],
  universe: ['SaaS', 'Data Infrastructure'],
  linkedinUrl: 'https://linkedin.com/company/databricks',
  twitterUrl: 'https://twitter.com/databricks',
  facebookUrl: 'https://facebook.com/databricks',
  crunchbaseUrl: 'https://crunchbase.com/organization/databricks',
  pitchbookUrl: 'https://pitchbook.com/profiles/databricks',
  harmonicUrl: null,

  foresightSummary: [
    'Databricks is a late-stage company that has raised $4.1B across multiple rounds, with its latest funding in Sep 2023.',
    'The company reports an estimated $1.6B in ARR and is experiencing rapid growth, with headcount increasing 14.5% over the past 6 months.',
    'Hiring activity is accelerating, driven by expansion into AI/ML and generative AI capabilities.',
    'With approximately $267K in revenue per employee, Databricks is scaling efficiently while competing aggressively with Snowflake in the data platform market.',
  ],

  fundingEvolution: [
    { stage: 'Series A', headcount: 45, growthPct: 0 },
    { stage: 'Series B', headcount: 180, growthPct: 55 },
    { stage: 'Series C', headcount: 650, growthPct: 80 },
    { stage: 'Series D', headcount: 2200, growthPct: 65 },
    { stage: 'Series E+', headcount: 6000, growthPct: 30 },
  ],

  fundingRounds: [
    {
      stage: 'Series I',
      round: 'Series I',
      date: 'Sep 14, 2023',
      raised: 500_000_000,
      postMoney: 43_000_000_000,
      leadInvestors: ['T. Rowe Price'],
      allInvestors: ['T. Rowe Price', 'Nvidia', 'Capital One', 'a16z', 'Fidelity', '+6'],
    },
    {
      stage: 'Series H',
      round: 'Series H',
      date: 'Aug 31, 2021',
      raised: 1_600_000_000,
      postMoney: 38_000_000_000,
      leadInvestors: ['Morgan Stanley'],
      allInvestors: ['Morgan Stanley', 'Baillie Gifford', 'UC Investments', 'Fidelity', '+8'],
    },
    {
      stage: 'Series G',
      round: 'Series G',
      date: 'Feb 1, 2021',
      raised: 1_000_000_000,
      postMoney: 28_000_000_000,
      leadInvestors: ['Franklin Templeton'],
      allInvestors: ['Franklin Templeton', 'Amazon', 'CapitalG', 'Salesforce Ventures', '+5'],
    },
  ],

  highlights: [
    'Reached $1.6B ARR with 60%+ YoY revenue growth',
    'Launched DBRX open-source LLM and MosaicML integration',
    'IPO expected in 2025-2026 at $40B+ valuation',
    'Strategic partnerships with AWS, Azure, and GCP',
  ],

  legalName: 'Databricks, Inc.',
  alsoKnownAs: [],
  secondaryHq: 'Amsterdam, Netherlands',
  companyType: null,
  acquiredBy: 'Andreessen Horowitz',

  keyPeople: [
    {
      id: 'ali-ghodsi',
      name: 'Ali Ghodsi',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/alighodsi',
      twitterUrl: 'https://twitter.com/aaborssafari',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'UC Berkeley', logoUrl: null, title: 'Adjunct Professor', duration: '6y' },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'ali-ghodsi-f',
      name: 'Ali Ghodsi',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/alighodsi',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'UC Berkeley', logoUrl: null, title: 'Adjunct Professor', duration: '6y' },
      ],
    },
    {
      id: 'ion-stoica-f',
      name: 'Ion Stoica',
      title: 'Co-Founder & Executive Chairman',
      location: 'Berkeley, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/ionstoica',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [{ company: 'UC Berkeley', logoUrl: null, title: 'Professor', duration: '18y' }],
    },
  ],

  email: 'info@databricks.com',
  phone: '+1 866 330 0121',

  similarCompanies: [
    {
      stage: 'Series I',
      round: 'Series I',
      date: 'Sep 14, 2023',
      raised: 500_000_000,
      postMoney: 43_000_000_000,
      leadInvestors: ['T. Rowe Price'],
      allInvestors: ['T. Rowe Price', '+6'],
    },
    {
      stage: 'Series H',
      round: 'Series H',
      date: 'Aug 31, 2021',
      raised: 1_600_000_000,
      postMoney: 38_000_000_000,
      leadInvestors: ['Morgan Stanley'],
      allInvestors: ['Morgan Stanley', '+8'],
    },
  ],
} as const satisfies ISourcingProfileData;

const FIGMA_PROFILE = {
  id: 'figma',
  name: 'Figma',
  logoUrl: 'https://logo.clearbit.com/figma.com',
  website: 'figma.com',
  description:
    'Figma is a collaborative design platform that enables teams to create, prototype, and handoff designs in real-time on the web.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'Design Tools', 'SaaS'],
  enrichedDate: 'Mar 8, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '42,100,000',
      delta: '+4.5%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '620,000',
      delta: '+28,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '540,000',
      delta: '+15,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '1,200-1,500',
      delta: '+90',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Dylan Field',
  founders: ['Dylan Field', 'Evan Wallace'],
  foundedYear: 2012,
  headcount: '1,200-1,500',
  totalRaised: 332_000_000,
  annualRevenue: 600_000_000,
  revenuePerEmployee: 444_000,
  hq: 'San Francisco, CA, USA',
  sector: ['Design Tools', 'SaaS'],
  industry: ['Software', 'Collaboration'],
  keywords: ['Design', 'Collaboration', 'Prototyping', 'Venture Capital'],
  universe: ['SaaS', 'DevTools'],
  linkedinUrl: 'https://linkedin.com/company/figma',
  twitterUrl: 'https://twitter.com/figma',
  facebookUrl: 'https://facebook.com/figma',
  crunchbaseUrl: 'https://crunchbase.com/organization/figma',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Figma is a late-stage private company valued at $12.5B following the terminated Adobe acquisition, having raised $332M across multiple rounds.',
    'The company reports approximately $600M in ARR and maintains steady growth, with headcount expanding 6.8% over the past 6 months.',
    'Hiring is stable as the company continues to invest in AI-powered design features and FigJam collaboration tools.',
    'With approximately $444K in revenue per employee, Figma is one of the most capital-efficient design tool companies in the market.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 8, growthPct: 0 },
    { stage: 'Series A', headcount: 30, growthPct: 40 },
    { stage: 'Series B', headcount: 120, growthPct: 65 },
    { stage: 'Series C', headcount: 450, growthPct: 80 },
    { stage: 'Series E+', headcount: 1350, growthPct: 20 },
  ],

  fundingRounds: [
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Jun 24, 2021',
      raised: 200_000_000,
      postMoney: 10_000_000_000,
      leadInvestors: ['Durable Capital Partners'],
      allInvestors: ['Durable Capital Partners', 'Greenoaks', 'Index Ventures', 'a16z', '+4'],
    },
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Apr 30, 2020',
      raised: 50_000_000,
      postMoney: 2_000_000_000,
      leadInvestors: ['a16z'],
      allInvestors: ['a16z', 'Sequoia', 'Greylock', 'Kleiner Perkins', '+3'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Feb 5, 2019',
      raised: 40_000_000,
      postMoney: 440_000_000,
      leadInvestors: ['Sequoia'],
      allInvestors: ['Sequoia', 'Greylock', 'KPCB', 'Index Ventures', '+2'],
    },
  ],

  highlights: [
    'Adobe acquisition ($20B) terminated in Dec 2023 — now independent',
    'Reached $600M ARR with 150%+ net dollar retention',
    'Launched AI-powered features including auto-layout suggestions',
    'Config 2024 conference drew 10,000+ attendees',
  ],

  legalName: 'Figma, Inc.',
  alsoKnownAs: [],
  secondaryHq: 'New York, NY, USA',
  companyType: null,
  acquiredBy: 'a16z',

  keyPeople: [
    {
      id: 'dylan-field',
      name: 'Dylan Field',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/dylanfield',
      twitterUrl: 'https://twitter.com/zoink',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Flipboard', logoUrl: null, title: 'Intern', duration: '3m' },
        {
          company: 'LinkedIn',
          logoUrl: 'https://logo.clearbit.com/linkedin.com',
          title: 'Intern',
          duration: '3m',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'dylan-field-f',
      name: 'Dylan Field',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/dylanfield',
      twitterUrl: 'https://twitter.com/zoink',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Flipboard', logoUrl: null, title: 'Intern', duration: '3m' }],
    },
    {
      id: 'evan-wallace-f',
      name: 'Evan Wallace',
      title: 'Co-Founder',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/evanwallace',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google',
          logoUrl: 'https://logo.clearbit.com/google.com',
          title: 'Software Engineer',
          duration: '1y',
        },
      ],
    },
  ],

  email: 'support@figma.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Jun 24, 2021',
      raised: 200_000_000,
      postMoney: 10_000_000_000,
      leadInvestors: ['Durable Capital Partners'],
      allInvestors: ['Durable Capital Partners', '+4'],
    },
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Apr 30, 2020',
      raised: 50_000_000,
      postMoney: 2_000_000_000,
      leadInvestors: ['a16z'],
      allInvestors: ['a16z', '+3'],
    },
  ],
} as const satisfies ISourcingProfileData;

const NOTION_PROFILE = {
  id: 'notion',
  name: 'Notion',
  logoUrl: 'https://logo.clearbit.com/notion.so',
  website: 'notion.so',
  description:
    'Notion is an all-in-one workspace for notes, docs, wikis, project management, and collaboration, used by teams and individuals worldwide.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'B2C', 'Productivity', 'SaaS'],
  enrichedDate: 'Mar 5, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '180,500,000',
      delta: '+2.8%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '340,000',
      delta: '+12,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '720,000',
      delta: '+24,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '600-800',
      delta: '+45',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Ivan Zhao',
  founders: ['Ivan Zhao', 'Simon Last'],
  foundedYear: 2013,
  headcount: '600-800',
  totalRaised: 343_000_000,
  annualRevenue: 250_000_000,
  revenuePerEmployee: 357_000,
  hq: 'San Francisco, CA, USA',
  sector: ['Productivity', 'SaaS'],
  industry: ['Software', 'Collaboration'],
  keywords: ['Workspace', 'Notes', 'Wiki', 'Venture Capital'],
  universe: ['SaaS'],
  linkedinUrl: 'https://linkedin.com/company/notionhq',
  twitterUrl: 'https://twitter.com/NotionHQ',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/notion-so',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Notion is a Series C company valued at $10B, having raised $343M across multiple rounds with its latest funding in Nov 2021.',
    'The company reports approximately $250M in ARR and maintains a lean team of 600-800 employees.',
    'Hiring is stable with a 6.2% growth rate over the past 6 months, primarily in engineering and AI roles.',
    'With approximately $357K in revenue per employee, Notion is one of the most efficient SaaS companies, driven by strong product-led growth.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 5, growthPct: 0 },
    { stage: 'Series A', headcount: 40, growthPct: 35 },
    { stage: 'Series B', headcount: 200, growthPct: 90 },
    { stage: 'Series C', headcount: 700, growthPct: 25 },
  ],

  fundingRounds: [
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Nov 3, 2021',
      raised: 275_000_000,
      postMoney: 10_000_000_000,
      leadInvestors: ['Coatue', 'Sequoia'],
      allInvestors: ['Coatue', 'Sequoia', 'Index Ventures', 'elad.com', '+4'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Apr 8, 2020',
      raised: 50_000_000,
      postMoney: 2_000_000_000,
      leadInvestors: ['Index Ventures'],
      allInvestors: ['Index Ventures', 'Elad Gil', 'First Round Capital', '+3'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'Jul 12, 2019',
      raised: 10_000_000,
      postMoney: 800_000_000,
      leadInvestors: [],
      allInvestors: ['Index Ventures', 'Elad Gil', '+2'],
    },
  ],

  highlights: [
    'Surpassed 100M+ users with strong PLG motion',
    'Launched Notion AI powered by LLM integration',
    'Enterprise segment growing 3x YoY',
    'Notion Calendar launched after Cron acquisition',
  ],

  legalName: 'Notion Labs, Inc.',
  alsoKnownAs: ['Notion Labs'],
  secondaryHq: 'New York, NY, USA',
  companyType: null,
  acquiredBy: 'Sequoia Capital',

  keyPeople: [
    {
      id: 'ivan-zhao',
      name: 'Ivan Zhao',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/ivanz',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Inkling', logoUrl: null, title: 'Designer', duration: '2y' }],
    },
  ],

  founderPeople: [
    {
      id: 'ivan-zhao-f',
      name: 'Ivan Zhao',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/ivanz',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Inkling', logoUrl: null, title: 'Designer', duration: '2y' }],
    },
    {
      id: 'simon-last-f',
      name: 'Simon Last',
      title: 'Co-Founder',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: null,
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google',
          logoUrl: 'https://logo.clearbit.com/google.com',
          title: 'Software Engineer',
          duration: '2y 3m',
        },
      ],
    },
  ],

  email: 'team@makenotion.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Nov 3, 2021',
      raised: 275_000_000,
      postMoney: 10_000_000_000,
      leadInvestors: ['Coatue'],
      allInvestors: ['Coatue', '+4'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Apr 8, 2020',
      raised: 50_000_000,
      postMoney: 2_000_000_000,
      leadInvestors: ['Index Ventures'],
      allInvestors: ['Index Ventures', '+3'],
    },
  ],
} as const satisfies ISourcingProfileData;

const VERCEL_PROFILE = {
  id: 'vercel',
  name: 'Vercel',
  logoUrl: 'https://logo.clearbit.com/vercel.com',
  website: 'vercel.com',
  description:
    'Vercel is a cloud platform for frontend developers, providing the best developer experience for building and deploying web applications with Next.js.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'DevTools', 'Cloud Platform'],
  enrichedDate: 'Mar 11, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '22,400,000',
      delta: '+7.2%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '210,000',
      delta: '+18,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '450,000',
      delta: '+32,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '500-700',
      delta: '+65',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Guillermo Rauch',
  founders: ['Guillermo Rauch'],
  foundedYear: 2015,
  headcount: '500-700',
  totalRaised: 563_000_000,
  annualRevenue: 200_000_000,
  revenuePerEmployee: 333_000,
  hq: 'San Francisco, CA, USA',
  sector: ['DevTools', 'Cloud Platform'],
  industry: ['Software', 'Web Development'],
  keywords: ['Next.js', 'Jamstack', 'Serverless', 'Venture Capital'],
  universe: ['SaaS', 'DevTools'],
  linkedinUrl: 'https://linkedin.com/company/vercel',
  twitterUrl: 'https://twitter.com/vercel',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/vercel',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Vercel is a Series D company valued at $3.25B, having raised $563M across multiple rounds with its latest funding in May 2024.',
    'The company reports approximately $200M in ARR, driven by strong adoption of Next.js and its AI SDK.',
    'Hiring is accelerating at 11.5% over the past 6 months, with a focus on infrastructure and AI engineering.',
    'With approximately $333K in revenue per employee, Vercel is efficiently scaling its developer-first platform while expanding enterprise adoption.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 12, growthPct: 0 },
    { stage: 'Series A', headcount: 50, growthPct: 55 },
    { stage: 'Series B', headcount: 180, growthPct: 70 },
    { stage: 'Series C', headcount: 400, growthPct: 50 },
    { stage: 'Series D', headcount: 620, growthPct: 25 },
  ],

  fundingRounds: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'May 16, 2024',
      raised: 250_000_000,
      postMoney: 3_250_000_000,
      leadInvestors: ['Accel'],
      allInvestors: ['Accel', 'CRV', 'Bedrock', 'GV', 'Geodesic', '+4'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Nov 22, 2021',
      raised: 150_000_000,
      postMoney: 2_500_000_000,
      leadInvestors: ['GIC'],
      allInvestors: ['GIC', 'Bedrock', 'Tiger Global', 'Greenoaks', '+5'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Jun 23, 2021',
      raised: 102_000_000,
      postMoney: 1_100_000_000,
      leadInvestors: ['Bedrock Capital'],
      allInvestors: ['Bedrock Capital', 'GV', 'Tiger Global', 'Flex Capital', '+3'],
    },
  ],

  highlights: [
    'Next.js is the most popular React framework with 120K+ GitHub stars',
    'Launched Vercel AI SDK driving enterprise AI adoption',
    'v0 AI tool generating 1M+ monthly code previews',
    'Enterprise customers include Walmart, Washington Post, and Nike',
  ],

  legalName: 'Vercel, Inc.',
  alsoKnownAs: ['ZEIT', 'Zeit'],
  secondaryHq: null,
  companyType: null,
  acquiredBy: 'Accel',

  keyPeople: [
    {
      id: 'guillermo-rauch',
      name: 'Guillermo Rauch',
      title: 'Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/guillermo-rauch',
      twitterUrl: 'https://twitter.com/raaborsk',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'LearnBoost', logoUrl: null, title: 'CEO & Co-Founder', duration: '3y' },
        {
          company: 'Automattic',
          logoUrl: 'https://logo.clearbit.com/automattic.com',
          title: 'Engineer',
          duration: '1y',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'guillermo-rauch-f',
      name: 'Guillermo Rauch',
      title: 'Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/guillermo-rauch',
      twitterUrl: 'https://twitter.com/raaborsk',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'LearnBoost', logoUrl: null, title: 'CEO & Co-Founder', duration: '3y' },
      ],
    },
  ],

  email: 'sales@vercel.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'May 16, 2024',
      raised: 250_000_000,
      postMoney: 3_250_000_000,
      leadInvestors: ['Accel'],
      allInvestors: ['Accel', '+4'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Nov 22, 2021',
      raised: 150_000_000,
      postMoney: 2_500_000_000,
      leadInvestors: ['GIC'],
      allInvestors: ['GIC', '+5'],
    },
  ],
} as const satisfies ISourcingProfileData;

const SCALEAI_PROFILE = {
  id: 'scaleai',
  name: 'Scale AI',
  logoUrl: 'https://logo.clearbit.com/scale.com',
  website: 'scale.com',
  description:
    'Scale AI provides high-quality training data and AI infrastructure for machine learning teams, enabling companies to build and deploy AI applications at scale.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'AI Data', 'ML Infrastructure'],
  enrichedDate: 'Mar 7, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '3,820,000',
      delta: '+9.1%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '180,000',
      delta: '+22,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '95,000',
      delta: '+8,500',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '600-800',
      delta: '+85',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Alexandr Wang',
  founders: ['Alexandr Wang', 'Lucy Guo'],
  foundedYear: 2016,
  headcount: '600-800',
  totalRaised: 1_020_000_000,
  annualRevenue: 750_000_000,
  revenuePerEmployee: 1_071_000,
  hq: 'San Francisco, CA, USA',
  sector: ['AI Data', 'ML Infrastructure'],
  industry: ['Artificial Intelligence', 'Data Services'],
  keywords: ['Training Data', 'RLHF', 'AI Safety', 'Defense', 'Venture Capital'],
  universe: ['AI Platform'],
  linkedinUrl: 'https://linkedin.com/company/scaleai',
  twitterUrl: 'https://twitter.com/scale_AI',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/scale-ai',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Scale AI is a late-stage company valued at $13.8B, having raised over $1B across multiple rounds with its latest funding in Aug 2024.',
    'The company reports approximately $750M in annual revenue, driven by surging demand for AI training data and RLHF services.',
    'Hiring is accelerating at 13.8% over the past 6 months, driven by government and enterprise AI contracts.',
    'With approximately $1.07M in revenue per employee, Scale AI demonstrates exceptional productivity, bolstered by its contract workforce model.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 10, growthPct: 0 },
    { stage: 'Series A', headcount: 50, growthPct: 55 },
    { stage: 'Series B', headcount: 150, growthPct: 80 },
    { stage: 'Series C', headcount: 350, growthPct: 60 },
    { stage: 'Series E+', headcount: 700, growthPct: 35 },
  ],

  fundingRounds: [
    {
      stage: 'Series F',
      round: 'Series F',
      date: 'Aug 5, 2024',
      raised: 1_000_000_000,
      postMoney: 13_800_000_000,
      leadInvestors: ['Accel'],
      allInvestors: ['Accel', 'Y Combinator', 'Amazon', 'Meta', 'Intel Capital', '+6'],
    },
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Apr 13, 2021',
      raised: 325_000_000,
      postMoney: 7_300_000_000,
      leadInvestors: ['Tiger Global'],
      allInvestors: ['Tiger Global', 'Dragoneer', 'Index Ventures', 'Coatue', '+4'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Dec 17, 2019',
      raised: 100_000_000,
      postMoney: 1_000_000_000,
      leadInvestors: ['Founders Fund'],
      allInvestors: ['Founders Fund', 'Accel', 'Index Ventures', 'Spark Capital', '+3'],
    },
  ],

  highlights: [
    'Secured $250M+ in US Department of Defense contracts',
    'Revenue grew 5x over two years driven by AI data demand',
    'Alexandr Wang became youngest self-made billionaire at 26',
    'Launched Scale Donovan for government AI applications',
  ],

  legalName: 'Scale AI, Inc.',
  alsoKnownAs: ['Scale'],
  secondaryHq: 'Washington, DC, USA',
  companyType: null,
  acquiredBy: 'Accel',

  keyPeople: [
    {
      id: 'alexandr-wang',
      name: 'Alexandr Wang',
      title: 'Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/alexandrwang',
      twitterUrl: 'https://twitter.com/alexandr_wang',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Quora',
          logoUrl: 'https://logo.clearbit.com/quora.com',
          title: 'Software Engineer',
          duration: '1y',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'alexandr-wang-f',
      name: 'Alexandr Wang',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/alexandrwang',
      twitterUrl: 'https://twitter.com/alexandr_wang',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Quora',
          logoUrl: 'https://logo.clearbit.com/quora.com',
          title: 'Software Engineer',
          duration: '1y',
        },
      ],
    },
    {
      id: 'lucy-guo-f',
      name: 'Lucy Guo',
      title: 'Co-Founder',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/lucyguo',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Snap',
          logoUrl: 'https://logo.clearbit.com/snap.com',
          title: 'Product Designer',
          duration: '1y 6m',
        },
      ],
    },
  ],

  email: 'sales@scale.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series F',
      round: 'Series F',
      date: 'Aug 5, 2024',
      raised: 1_000_000_000,
      postMoney: 13_800_000_000,
      leadInvestors: ['Accel'],
      allInvestors: ['Accel', '+6'],
    },
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Apr 13, 2021',
      raised: 325_000_000,
      postMoney: 7_300_000_000,
      leadInvestors: ['Tiger Global'],
      allInvestors: ['Tiger Global', '+4'],
    },
  ],
} as const satisfies ISourcingProfileData;

const ANDURIL_PROFILE = {
  id: 'anduril',
  name: 'Anduril',
  logoUrl: 'https://logo.clearbit.com/anduril.com',
  website: 'anduril.com',
  description:
    'Anduril Industries builds next-generation defense technology, combining AI and sensor fusion to provide autonomous systems for national security.',
  tags: ['Active', 'Private', 'Growth', 'B2G', 'Defense Tech', 'Autonomous Systems'],
  enrichedDate: 'Mar 9, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '1,450,000',
      delta: '+11.3%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '125,000',
      delta: '+19,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '65,000',
      delta: '+7,200',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '2,500-3,500',
      delta: '+380',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Brian Schimpf',
  founders: ['Palmer Luckey', 'Brian Schimpf', '+3'],
  foundedYear: 2017,
  headcount: '2,500-3,500',
  totalRaised: 3_700_000_000,
  annualRevenue: 900_000_000,
  revenuePerEmployee: 300_000,
  hq: 'Costa Mesa, CA, USA',
  sector: ['Defense Tech', 'Autonomous Systems'],
  industry: ['Defense', 'Aerospace'],
  keywords: ['Defense', 'Autonomy', 'Counter-UAS', 'Government Contracts'],
  universe: ['Defense Tech'],
  linkedinUrl: 'https://linkedin.com/company/anduril',
  twitterUrl: 'https://twitter.com/anaborril',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/anduril',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Anduril is a late-stage defense tech company valued at $14B, having raised $3.7B across multiple rounds with its latest funding in Aug 2024.',
    'The company reports approximately $900M in annual revenue from government defense contracts and is scaling rapidly.',
    'Hiring is accelerating at 16.2% over the past 6 months, driven by new contract wins and manufacturing expansion.',
    'With approximately $300K in revenue per employee, Anduril is building a modern defense prime contractor challenging incumbents like Lockheed Martin and Raytheon.',
  ],

  fundingEvolution: [
    { stage: 'Series A', headcount: 50, growthPct: 0 },
    { stage: 'Series B', headcount: 200, growthPct: 70 },
    { stage: 'Series C', headcount: 800, growthPct: 90 },
    { stage: 'Series D', headcount: 1800, growthPct: 65 },
    { stage: 'Series E+', headcount: 3000, growthPct: 40 },
  ],

  fundingRounds: [
    {
      stage: 'Series F',
      round: 'Series F',
      date: 'Aug 7, 2024',
      raised: 1_500_000_000,
      postMoney: 14_000_000_000,
      leadInvestors: ['Founders Fund'],
      allInvestors: ['Founders Fund', 'Sands Capital', 'a16z', 'Lux Capital', 'WCM', '+5'],
    },
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Dec 12, 2022',
      raised: 1_480_000_000,
      postMoney: 8_500_000_000,
      leadInvestors: ['Founders Fund'],
      allInvestors: ['Founders Fund', 'a16z', '8VC', 'Elad Gil', 'Valor Equity Partners', '+7'],
    },
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Jun 15, 2021',
      raised: 450_000_000,
      postMoney: 4_600_000_000,
      leadInvestors: ['a16z'],
      allInvestors: ['a16z', 'Founders Fund', 'Lux Capital', 'Valor', '+4'],
    },
  ],

  highlights: [
    'Secured $1.5B Series F at $14B valuation — largest defense tech raise',
    'Won multi-billion dollar DoD autonomous systems contracts',
    'Lattice OS deployed across US military branches',
    'Building arsenal of autonomous drones and counter-UAS systems',
  ],

  legalName: 'Anduril Industries, Inc.',
  alsoKnownAs: [],
  secondaryHq: 'Washington, DC, USA',
  companyType: null,
  acquiredBy: 'Founders Fund',

  keyPeople: [
    {
      id: 'brian-schimpf',
      name: 'Brian Schimpf',
      title: 'Co-Founder & CEO',
      location: 'Costa Mesa, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/bschimpf',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Palantir',
          logoUrl: 'https://logo.clearbit.com/palantir.com',
          title: 'Engineering Director',
          duration: '5y',
        },
      ],
    },
    {
      id: 'palmer-luckey',
      name: 'Palmer Luckey',
      title: 'Co-Founder',
      location: 'Costa Mesa, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/palmerluckey',
      twitterUrl: 'https://twitter.com/PalmerLuckey',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Oculus VR', logoUrl: null, title: 'Founder', duration: '4y 2m' }],
    },
  ],

  founderPeople: [
    {
      id: 'palmer-luckey-f',
      name: 'Palmer Luckey',
      title: 'Co-Founder',
      location: 'Costa Mesa, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/palmerluckey',
      twitterUrl: 'https://twitter.com/PalmerLuckey',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Oculus VR', logoUrl: null, title: 'Founder', duration: '4y 2m' }],
    },
    {
      id: 'brian-schimpf-f',
      name: 'Brian Schimpf',
      title: 'Co-Founder & CEO',
      location: 'Costa Mesa, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/bschimpf',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Palantir',
          logoUrl: 'https://logo.clearbit.com/palantir.com',
          title: 'Engineering Director',
          duration: '5y',
        },
      ],
    },
  ],

  email: 'info@anduril.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series F',
      round: 'Series F',
      date: 'Aug 7, 2024',
      raised: 1_500_000_000,
      postMoney: 14_000_000_000,
      leadInvestors: ['Founders Fund'],
      allInvestors: ['Founders Fund', '+5'],
    },
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Dec 12, 2022',
      raised: 1_480_000_000,
      postMoney: 8_500_000_000,
      leadInvestors: ['Founders Fund'],
      allInvestors: ['Founders Fund', '+7'],
    },
  ],
} as const satisfies ISourcingProfileData;

const RIPPLING_PROFILE = {
  id: 'rippling',
  name: 'Rippling',
  logoUrl: 'https://logo.clearbit.com/rippling.com',
  website: 'rippling.com',
  description:
    'Rippling is a workforce management platform that unifies HR, IT, and finance into a single system, enabling businesses to manage employees from hire to retire.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'HR Tech', 'SaaS'],
  enrichedDate: 'Mar 14, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '8,200,000',
      delta: '+5.8%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '155,000',
      delta: '+14,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '42,000',
      delta: '+3,200',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '2,800-3,500',
      delta: '+310',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Parker Conrad',
  founders: ['Parker Conrad', 'Prasanna Sankar'],
  foundedYear: 2016,
  headcount: '2,800-3,500',
  totalRaised: 1_200_000_000,
  annualRevenue: 350_000_000,
  revenuePerEmployee: 111_000,
  hq: 'San Francisco, CA, USA',
  sector: ['HR Tech', 'SaaS'],
  industry: ['Human Resources', 'Software'],
  keywords: ['HRIS', 'Payroll', 'IT Management', 'Venture Capital'],
  universe: ['SaaS'],
  linkedinUrl: 'https://linkedin.com/company/rippling',
  twitterUrl: 'https://twitter.com/Rippling',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/rippling',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Rippling is a Series E company valued at $13.5B, having raised $1.2B across multiple rounds with its latest funding in Mar 2024.',
    'The company reports approximately $350M in ARR with strong growth driven by its compound startup strategy across HR, IT, and finance.',
    'Hiring is accelerating at 10.8% over the past 6 months as the company expands its product surface area.',
    'With approximately $111K in revenue per employee, Rippling is investing heavily in product development to build its all-in-one workforce platform vision.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 15, growthPct: 0 },
    { stage: 'Series A', headcount: 80, growthPct: 60 },
    { stage: 'Series B', headcount: 400, growthPct: 95 },
    { stage: 'Series C', headcount: 1200, growthPct: 70 },
    { stage: 'Series E+', headcount: 3100, growthPct: 30 },
  ],

  fundingRounds: [
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Mar 11, 2024',
      raised: 200_000_000,
      postMoney: 13_500_000_000,
      leadInvestors: ['Coatue'],
      allInvestors: ['Coatue', 'Founders Fund', 'Greenoaks', 'Bedrock', '+5'],
    },
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'May 11, 2023',
      raised: 500_000_000,
      postMoney: 11_250_000_000,
      leadInvestors: ['Greenoaks'],
      allInvestors: ['Greenoaks', 'Coatue', 'Founders Fund', 'Sequoia', '+4'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Oct 6, 2022',
      raised: 250_000_000,
      postMoney: 6_000_000_000,
      leadInvestors: ['Bedrock Capital'],
      allInvestors: ['Bedrock Capital', 'Kleiner Perkins', 'YC', '+4'],
    },
  ],

  highlights: [
    'Reached $350M+ ARR growing 100%+ YoY',
    'Expanding into ERP and financial management',
    'Named to Forbes Cloud 100 for third consecutive year',
    'Parker Conrad pioneering the "compound startup" strategy',
  ],

  legalName: 'Rippling People Center, Inc.',
  alsoKnownAs: [],
  secondaryHq: 'Bengaluru, India',
  companyType: null,
  acquiredBy: 'Coatue',

  keyPeople: [
    {
      id: 'parker-conrad',
      name: 'Parker Conrad',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/parkerconrad',
      twitterUrl: 'https://twitter.com/parkerconrad',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Zenefits', logoUrl: null, title: 'CEO & Co-Founder', duration: '3y 8m' },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'parker-conrad-f',
      name: 'Parker Conrad',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/parkerconrad',
      twitterUrl: 'https://twitter.com/parkerconrad',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Zenefits', logoUrl: null, title: 'CEO & Co-Founder', duration: '3y 8m' },
      ],
    },
    {
      id: 'prasanna-sankar-f',
      name: 'Prasanna Sankar',
      title: 'Co-Founder & CTO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/prasannasankar',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Zenefits', logoUrl: null, title: 'Engineering Lead', duration: '2y' }],
    },
  ],

  email: 'sales@rippling.com',
  phone: '+1 415 849 5900',

  similarCompanies: [
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Mar 11, 2024',
      raised: 200_000_000,
      postMoney: 13_500_000_000,
      leadInvestors: ['Coatue'],
      allInvestors: ['Coatue', '+5'],
    },
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'May 11, 2023',
      raised: 500_000_000,
      postMoney: 11_250_000_000,
      leadInvestors: ['Greenoaks'],
      allInvestors: ['Greenoaks', '+4'],
    },
  ],
} as const satisfies ISourcingProfileData;

const RAMP_PROFILE = {
  id: 'ramp',
  name: 'Ramp',
  logoUrl: 'https://logo.clearbit.com/ramp.com',
  website: 'ramp.com',
  description:
    'Ramp is a corporate card and spend management platform that helps businesses control spending, automate expense reports, and close books faster.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'Fintech', 'Spend Management'],
  enrichedDate: 'Mar 6, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '5,600,000',
      delta: '+8.9%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '92,000',
      delta: '+11,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '38,000',
      delta: '+4,500',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '700-900',
      delta: '+95',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Eric Glyman',
  founders: ['Eric Glyman', 'Karim Atiyeh'],
  foundedYear: 2019,
  headcount: '700-900',
  totalRaised: 970_000_000,
  annualRevenue: 400_000_000,
  revenuePerEmployee: 500_000,
  hq: 'New York, NY, USA',
  sector: ['Fintech', 'Spend Management'],
  industry: ['Financial Services', 'Software'],
  keywords: ['Corporate Card', 'Expense Management', 'AP Automation', 'Venture Capital'],
  universe: ['Fintech', 'SaaS'],
  linkedinUrl: 'https://linkedin.com/company/raboramp',
  twitterUrl: 'https://twitter.com/tryramp',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/ramp-2',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Ramp is a Series D company valued at $7.65B, having raised $970M across multiple rounds with its latest funding in Mar 2024.',
    'The company reports approximately $400M in annualized revenue and is one of the fastest-growing fintech companies in the US.',
    'Hiring is accelerating at 12.1% over the past 6 months, driven by new product launches in procurement and travel.',
    'With approximately $500K in revenue per employee, Ramp demonstrates strong capital efficiency while expanding its finance automation platform.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 8, growthPct: 0 },
    { stage: 'Series A', headcount: 60, growthPct: 80 },
    { stage: 'Series B', headcount: 250, growthPct: 95 },
    { stage: 'Series C', headcount: 550, growthPct: 55 },
    { stage: 'Series D', headcount: 800, growthPct: 25 },
  ],

  fundingRounds: [
    {
      stage: 'Series D',
      round: 'Series D-2',
      date: 'Mar 28, 2024',
      raised: 150_000_000,
      postMoney: 7_650_000_000,
      leadInvestors: ['Khosla Ventures'],
      allInvestors: ['Khosla Ventures', 'Founders Fund', 'Thrive Capital', 'Sands Capital', '+4'],
    },
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Aug 18, 2023',
      raised: 300_000_000,
      postMoney: 5_800_000_000,
      leadInvestors: ['Founders Fund', 'Thrive Capital'],
      allInvestors: ['Founders Fund', 'Thrive Capital', 'General Catalyst', 'Goldman Sachs', '+5'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Mar 29, 2022',
      raised: 200_000_000,
      postMoney: 8_100_000_000,
      leadInvestors: ['Founders Fund'],
      allInvestors: ['Founders Fund', 'Stripe', 'Goldman Sachs', 'D1 Capital', '+4'],
    },
  ],

  highlights: [
    'Revenue grew 3.5x YoY reaching $400M+ annualized',
    'Processing $30B+ in annualized transaction volume',
    'Launched Ramp Travel and Ramp Procurement verticals',
    'Named fastest-growing corporate card in the US',
  ],

  legalName: 'Ramp Business Corporation',
  alsoKnownAs: [],
  secondaryHq: 'San Francisco, CA, USA',
  companyType: null,
  acquiredBy: 'Founders Fund',

  keyPeople: [
    {
      id: 'eric-glyman',
      name: 'Eric Glyman',
      title: 'Co-Founder & CEO',
      location: 'New York, NY, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/ericglyman',
      twitterUrl: 'https://twitter.com/EricGlyman',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Paribus', logoUrl: null, title: 'CEO & Co-Founder', duration: '3y 5m' },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'eric-glyman-f',
      name: 'Eric Glyman',
      title: 'Co-Founder & CEO',
      location: 'New York, NY, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/ericglyman',
      twitterUrl: 'https://twitter.com/EricGlyman',
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Paribus', logoUrl: null, title: 'CEO & Co-Founder', duration: '3y 5m' },
      ],
    },
    {
      id: 'karim-atiyeh-f',
      name: 'Karim Atiyeh',
      title: 'Co-Founder & CTO',
      location: 'New York, NY, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/karimatiyeh',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        { company: 'Paribus', logoUrl: null, title: 'CTO & Co-Founder', duration: '3y 5m' },
      ],
    },
  ],

  email: 'support@ramp.com',
  phone: '+1 844 727 6714',

  similarCompanies: [
    {
      stage: 'Series D',
      round: 'Series D-2',
      date: 'Mar 28, 2024',
      raised: 150_000_000,
      postMoney: 7_650_000_000,
      leadInvestors: ['Khosla Ventures'],
      allInvestors: ['Khosla Ventures', '+4'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Mar 29, 2022',
      raised: 200_000_000,
      postMoney: 8_100_000_000,
      leadInvestors: ['Founders Fund'],
      allInvestors: ['Founders Fund', '+4'],
    },
  ],
} as const satisfies ISourcingProfileData;

const BREX_PROFILE = {
  id: 'brex',
  name: 'Brex',
  logoUrl: 'https://logo.clearbit.com/brex.com',
  website: 'brex.com',
  description:
    'Brex is a financial operating system for growing businesses, offering corporate cards, expense management, bill pay, and business accounts.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'Fintech', 'Corporate Finance'],
  enrichedDate: 'Mar 4, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '4,120,000',
      delta: '+2.1%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '110,000',
      delta: '+5,500',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '48,000',
      delta: '+1,800',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '1,000-1,200',
      delta: '-40',
      deltaPositive: false,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Pedro Franceschi',
  founders: ['Pedro Franceschi', 'Henrique Dubugras'],
  foundedYear: 2017,
  headcount: '1,000-1,200',
  totalRaised: 1_500_000_000,
  annualRevenue: 320_000_000,
  revenuePerEmployee: 290_000,
  hq: 'San Francisco, CA, USA',
  sector: ['Fintech', 'Corporate Finance'],
  industry: ['Financial Services', 'Banking'],
  keywords: ['Corporate Card', 'Spend Management', 'Banking', 'Venture Capital'],
  universe: ['Fintech'],
  linkedinUrl: 'https://linkedin.com/company/braborex',
  twitterUrl: 'https://twitter.com/braborex',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/brex',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Brex is a late-stage fintech company valued at $12.3B (at peak, now marked down to ~$6B), having raised $1.5B across multiple rounds.',
    'The company reports approximately $320M in ARR and has refocused on enterprise customers after exiting the SMB segment in 2022.',
    'Headcount has decreased 3.5% over the past 6 months as the company optimizes for profitability after earlier cuts.',
    'With approximately $290K in revenue per employee, Brex is working toward a path to profitability while competing with Ramp in the enterprise spend management space.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 12, growthPct: 0 },
    { stage: 'Series A', headcount: 80, growthPct: 95 },
    { stage: 'Series B', headcount: 350, growthPct: 110 },
    { stage: 'Series C', headcount: 1200, growthPct: 60 },
    { stage: 'Series D', headcount: 1100, growthPct: -8 },
  ],

  fundingRounds: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Jan 11, 2022',
      raised: 300_000_000,
      postMoney: 12_300_000_000,
      leadInvestors: ['Tiger Global', 'Greenoaks'],
      allInvestors: ['Tiger Global', 'Greenoaks', 'Dragoneer', 'a16z', '+5'],
    },
    {
      stage: 'Series C',
      round: 'Series C-3',
      date: 'Oct 5, 2021',
      raised: 150_000_000,
      postMoney: 7_400_000_000,
      leadInvestors: ['Tiger Global'],
      allInvestors: ['Tiger Global', 'Lone Pine Capital', 'DST Global', '+3'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Apr 22, 2021',
      raised: 425_000_000,
      postMoney: 7_400_000_000,
      leadInvestors: ['Tiger Global'],
      allInvestors: ['Tiger Global', 'a16z', 'Ribbit Capital', 'Y Combinator', '+6'],
    },
  ],

  highlights: [
    'Pivoted from SMB to enterprise focus in 2022',
    'Revenue at $320M ARR growing 40% YoY post-pivot',
    'Launched Brex AI for automated expense categorization',
    'Approaching profitability with focus on unit economics',
  ],

  legalName: 'Brex Inc.',
  alsoKnownAs: [],
  secondaryHq: 'New York, NY, USA',
  companyType: null,
  acquiredBy: 'Tiger Global',

  keyPeople: [
    {
      id: 'pedro-franceschi',
      name: 'Pedro Franceschi',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/pedrofranceschi',
      twitterUrl: 'https://twitter.com/pefranceschi',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Pagar.me', logoUrl: null, title: 'Co-Founder & CTO', duration: '4y' }],
    },
  ],

  founderPeople: [
    {
      id: 'pedro-franceschi-f',
      name: 'Pedro Franceschi',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/pedrofranceschi',
      twitterUrl: 'https://twitter.com/pefranceschi',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Pagar.me', logoUrl: null, title: 'Co-Founder & CTO', duration: '4y' }],
    },
    {
      id: 'henrique-dubugras-f',
      name: 'Henrique Dubugras',
      title: 'Co-Founder',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/hdubugras',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Pagar.me', logoUrl: null, title: 'Co-Founder & CEO', duration: '4y' }],
    },
  ],

  email: 'support@brex.com',
  phone: '+1 833 271 2739',

  similarCompanies: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Jan 11, 2022',
      raised: 300_000_000,
      postMoney: 12_300_000_000,
      leadInvestors: ['Tiger Global'],
      allInvestors: ['Tiger Global', '+5'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Apr 22, 2021',
      raised: 425_000_000,
      postMoney: 7_400_000_000,
      leadInvestors: ['Tiger Global'],
      allInvestors: ['Tiger Global', '+6'],
    },
  ],
} as const satisfies ISourcingProfileData;

const PLAID_PROFILE = {
  id: 'plaid',
  name: 'Plaid',
  logoUrl: 'https://logo.clearbit.com/plaid.com',
  website: 'plaid.com',
  description:
    'Plaid is a fintech infrastructure company that enables applications to connect with users bank accounts, powering thousands of financial apps.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'Fintech', 'Infrastructure'],
  enrichedDate: 'Mar 3, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '6,400,000',
      delta: '+3.5%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '195,000',
      delta: '+8,500',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '72,000',
      delta: '+2,800',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '900-1,100',
      delta: '+55',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Zach Perret',
  founders: ['Zach Perret', 'William Hockey'],
  foundedYear: 2013,
  headcount: '900-1,100',
  totalRaised: 734_000_000,
  annualRevenue: 500_000_000,
  revenuePerEmployee: 500_000,
  hq: 'San Francisco, CA, USA',
  sector: ['Fintech', 'Infrastructure'],
  industry: ['Financial Services', 'API Platform'],
  keywords: ['Open Banking', 'Financial Data', 'API', 'Venture Capital'],
  universe: ['Fintech', 'Developer Platform'],
  linkedinUrl: 'https://linkedin.com/company/plaid',
  twitterUrl: 'https://twitter.com/plaid',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/plaid',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Plaid is a late-stage fintech infrastructure company, valued at $13.4B, having raised $734M with its latest funding in Apr 2021.',
    'The company reports approximately $500M in ARR and connects 12,000+ financial institutions to thousands of apps.',
    'Hiring is stable at 5.5% growth over the past 6 months as the company focuses on profitability and new product lines.',
    'With approximately $500K in revenue per employee, Plaid is a highly efficient infrastructure business powering the fintech ecosystem.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 8, growthPct: 0 },
    { stage: 'Series A', headcount: 35, growthPct: 50 },
    { stage: 'Series B', headcount: 150, growthPct: 85 },
    { stage: 'Series C', headcount: 500, growthPct: 70 },
    { stage: 'Series D', headcount: 1000, growthPct: 20 },
  ],

  fundingRounds: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Apr 7, 2021',
      raised: 425_000_000,
      postMoney: 13_400_000_000,
      leadInvestors: ['Altimeter Capital'],
      allInvestors: ['Altimeter Capital', 'Silver Lake', 'Ribbit Capital', 'a16z', '+5'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Dec 5, 2018',
      raised: 250_000_000,
      postMoney: 2_650_000_000,
      leadInvestors: ['Mary Meeker (Kleiner Perkins)'],
      allInvestors: ['Kleiner Perkins', 'Index Ventures', 'a16z', 'Goldman Sachs', '+3'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Jun 14, 2016',
      raised: 44_000_000,
      postMoney: 200_000_000,
      leadInvestors: ['NEA'],
      allInvestors: ['NEA', 'Goldman Sachs', 'Spark Capital', '+2'],
    },
  ],

  highlights: [
    'Visa acquisition ($5.3B) blocked by DOJ — remained independent',
    'Connected to 12,000+ financial institutions globally',
    'Expanded into identity verification and income verification',
    'Approaching profitability with strong unit economics',
  ],

  legalName: 'Plaid Inc.',
  alsoKnownAs: [],
  secondaryHq: 'Salt Lake City, UT, USA',
  companyType: null,
  acquiredBy: 'NEA',

  keyPeople: [
    {
      id: 'zach-perret',
      name: 'Zach Perret',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/zachperret',
      twitterUrl: 'https://twitter.com/zachperret',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Bain & Company',
          logoUrl: 'https://logo.clearbit.com/bain.com',
          title: 'Consultant',
          duration: '2y',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'zach-perret-f',
      name: 'Zach Perret',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/zachperret',
      twitterUrl: 'https://twitter.com/zachperret',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Bain & Company',
          logoUrl: 'https://logo.clearbit.com/bain.com',
          title: 'Consultant',
          duration: '2y',
        },
      ],
    },
    {
      id: 'william-hockey-f',
      name: 'William Hockey',
      title: 'Co-Founder',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/williamhockey',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Column', logoUrl: null, title: 'CEO & Founder', duration: '2y 6m' }],
    },
  ],

  email: 'support@plaid.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Apr 7, 2021',
      raised: 425_000_000,
      postMoney: 13_400_000_000,
      leadInvestors: ['Altimeter Capital'],
      allInvestors: ['Altimeter Capital', '+5'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Dec 5, 2018',
      raised: 250_000_000,
      postMoney: 2_650_000_000,
      leadInvestors: ['Kleiner Perkins'],
      allInvestors: ['Kleiner Perkins', '+3'],
    },
  ],
} as const satisfies ISourcingProfileData;

const AIRTABLE_PROFILE = {
  id: 'airtable',
  name: 'Airtable',
  logoUrl: 'https://logo.clearbit.com/airtable.com',
  website: 'airtable.com',
  description:
    'Airtable is a low-code platform that enables teams to build applications and workflows on top of a flexible relational database, bridging spreadsheets and databases.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'No-Code', 'SaaS'],
  enrichedDate: 'Mar 2, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '15,200,000',
      delta: '+1.9%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '170,000',
      delta: '+4,200',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '95,000',
      delta: '+1,800',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '800-1,000',
      delta: '-25',
      deltaPositive: false,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Howie Liu',
  founders: ['Howie Liu', 'Andrew Ofstad', 'Emmett Nicholas'],
  foundedYear: 2012,
  headcount: '800-1,000',
  totalRaised: 1_360_000_000,
  annualRevenue: 280_000_000,
  revenuePerEmployee: 311_000,
  hq: 'San Francisco, CA, USA',
  sector: ['No-Code', 'SaaS'],
  industry: ['Software', 'Productivity'],
  keywords: ['Low-Code', 'Database', 'Workflow Automation', 'Venture Capital'],
  universe: ['SaaS'],
  linkedinUrl: 'https://linkedin.com/company/airtable',
  twitterUrl: 'https://twitter.com/airtable',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/airtable',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Airtable is a late-stage low-code platform valued at $11.7B (at peak), having raised $1.36B across multiple rounds.',
    'The company reports approximately $280M in ARR with a shift toward enterprise customers and AI-powered automations.',
    'Headcount has decreased slightly (-2.8% over 6 months) following 2023 layoffs, as the company optimizes for efficiency.',
    'With approximately $311K in revenue per employee, Airtable is refocusing on its enterprise motion and profitability path.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 6, growthPct: 0 },
    { stage: 'Series A', headcount: 40, growthPct: 50 },
    { stage: 'Series B', headcount: 200, growthPct: 90 },
    { stage: 'Series C', headcount: 700, growthPct: 65 },
    { stage: 'Series E+', headcount: 900, growthPct: -5 },
  ],

  fundingRounds: [
    {
      stage: 'Series F',
      round: 'Series F',
      date: 'Dec 13, 2021',
      raised: 735_000_000,
      postMoney: 11_700_000_000,
      leadInvestors: ['Greenoaks Capital'],
      allInvestors: ['Greenoaks Capital', 'Coatue', 'CRV', 'Caffeinated Capital', '+6'],
    },
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Mar 15, 2021',
      raised: 270_000_000,
      postMoney: 5_770_000_000,
      leadInvestors: ['Greenoaks Capital'],
      allInvestors: ['Greenoaks Capital', 'WndrCo', 'Thrive Capital', '+4'],
    },
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Sep 14, 2020',
      raised: 185_000_000,
      postMoney: 2_585_000_000,
      leadInvestors: ['Thrive Capital'],
      allInvestors: ['Thrive Capital', 'D1 Capital', 'Coatue', 'Benchmark', '+3'],
    },
  ],

  highlights: [
    'Over 450,000 organizations using the platform',
    'Launched AI-powered automations and formula generation',
    'Enterprise segment growing 2x faster than SMB',
    'Workforce optimization in 2023 improved unit economics',
  ],

  legalName: 'Formagrid Inc. (dba Airtable)',
  alsoKnownAs: ['Formagrid'],
  secondaryHq: 'New York, NY, USA',
  companyType: null,
  acquiredBy: 'Greenoaks Capital',

  keyPeople: [
    {
      id: 'howie-liu',
      name: 'Howie Liu',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/howieliu',
      twitterUrl: 'https://twitter.com/howietl',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Salesforce',
          logoUrl: 'https://logo.clearbit.com/salesforce.com',
          title: 'Product Manager',
          duration: '2y',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'howie-liu-f',
      name: 'Howie Liu',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/howieliu',
      twitterUrl: 'https://twitter.com/howietl',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Salesforce',
          logoUrl: 'https://logo.clearbit.com/salesforce.com',
          title: 'Product Manager',
          duration: '2y',
        },
      ],
    },
    {
      id: 'andrew-ofstad-f',
      name: 'Andrew Ofstad',
      title: 'Co-Founder & CPO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/andrewofstad',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google',
          logoUrl: 'https://logo.clearbit.com/google.com',
          title: 'Product Manager',
          duration: '3y',
        },
      ],
    },
  ],

  email: 'support@airtable.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series F',
      round: 'Series F',
      date: 'Dec 13, 2021',
      raised: 735_000_000,
      postMoney: 11_700_000_000,
      leadInvestors: ['Greenoaks Capital'],
      allInvestors: ['Greenoaks Capital', '+6'],
    },
    {
      stage: 'Series E',
      round: 'Series E',
      date: 'Mar 15, 2021',
      raised: 270_000_000,
      postMoney: 5_770_000_000,
      leadInvestors: ['Greenoaks Capital'],
      allInvestors: ['Greenoaks Capital', '+4'],
    },
  ],
} as const satisfies ISourcingProfileData;

const RETOOL_PROFILE = {
  id: 'retool',
  name: 'Retool',
  logoUrl: 'https://logo.clearbit.com/retool.com',
  website: 'retool.com',
  description:
    'Retool is a development platform for building internal tools quickly, enabling developers to drag-and-drop components and connect to any database or API.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'DevTools', 'Internal Tools'],
  enrichedDate: 'Mar 13, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '4,500,000',
      delta: '+4.2%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '68,000',
      delta: '+5,500',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '55,000',
      delta: '+3,200',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '400-500',
      delta: '+30',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'David Hsu',
  founders: ['David Hsu'],
  foundedYear: 2017,
  headcount: '400-500',
  totalRaised: 445_000_000,
  annualRevenue: 120_000_000,
  revenuePerEmployee: 266_000,
  hq: 'San Francisco, CA, USA',
  sector: ['DevTools', 'Internal Tools'],
  industry: ['Software', 'Low-Code'],
  keywords: ['Internal Tools', 'Low-Code', 'Developer Platform', 'Venture Capital'],
  universe: ['SaaS', 'DevTools'],
  linkedinUrl: 'https://linkedin.com/company/retool',
  twitterUrl: 'https://twitter.com/retaborool',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/retool',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Retool is a Series C company valued at $3.2B, having raised $445M across multiple rounds with its latest funding in Dec 2021.',
    'The company reports approximately $120M in ARR and serves thousands of companies building internal tools.',
    'Hiring is stable at 6.8% growth over the past 6 months, with continued investment in AI capabilities.',
    'With approximately $266K in revenue per employee, Retool is efficiently scaling its developer-focused platform in the internal tools category it helped define.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 6, growthPct: 0 },
    { stage: 'Series A', headcount: 35, growthPct: 55 },
    { stage: 'Series B', headcount: 150, growthPct: 80 },
    { stage: 'Series C', headcount: 450, growthPct: 35 },
  ],

  fundingRounds: [
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Dec 15, 2021',
      raised: 200_000_000,
      postMoney: 3_200_000_000,
      leadInvestors: ['a16z'],
      allInvestors: ['a16z', 'Sequoia', 'Y Combinator', 'Elad Gil', '+3'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Apr 23, 2021',
      raised: 135_000_000,
      postMoney: 1_150_000_000,
      leadInvestors: ['a16z'],
      allInvestors: ['a16z', 'Sequoia', 'Stripe', 'Patrick Collison', '+4'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'Oct 3, 2019',
      raised: 21_000_000,
      postMoney: 100_000_000,
      leadInvestors: ['Sequoia'],
      allInvestors: ['Sequoia', 'Y Combinator', 'Elad Gil', '+2'],
    },
  ],

  highlights: [
    'Used by 27,000+ organizations for internal tools',
    'Launched Retool AI with LLM integration capabilities',
    'Strong developer community with 500+ template library',
    'Net dollar retention exceeding 130%',
  ],

  legalName: 'Retool, Inc.',
  alsoKnownAs: [],
  secondaryHq: null,
  companyType: null,
  acquiredBy: 'a16z',

  keyPeople: [
    {
      id: 'david-hsu',
      name: 'David Hsu',
      title: 'Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/dhsu',
      twitterUrl: 'https://twitter.com/davorhsu',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Palantir',
          logoUrl: 'https://logo.clearbit.com/palantir.com',
          title: 'Forward Deployed Engineer',
          duration: '1y 8m',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'david-hsu-f',
      name: 'David Hsu',
      title: 'Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/dhsu',
      twitterUrl: 'https://twitter.com/davorhsu',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Palantir',
          logoUrl: 'https://logo.clearbit.com/palantir.com',
          title: 'Forward Deployed Engineer',
          duration: '1y 8m',
        },
      ],
    },
  ],

  email: 'support@retool.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Dec 15, 2021',
      raised: 200_000_000,
      postMoney: 3_200_000_000,
      leadInvestors: ['a16z'],
      allInvestors: ['a16z', '+3'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Apr 23, 2021',
      raised: 135_000_000,
      postMoney: 1_150_000_000,
      leadInvestors: ['a16z'],
      allInvestors: ['a16z', '+4'],
    },
  ],
} as const satisfies ISourcingProfileData;

const POSTHOG_PROFILE = {
  id: 'posthog',
  name: 'PostHog',
  logoUrl: 'https://logo.clearbit.com/posthog.com',
  website: 'posthog.com',
  description:
    'PostHog is an open-source product analytics platform that helps engineers understand user behavior with event tracking, session replay, feature flags, and A/B testing.',
  tags: ['Active', 'Private', 'Startup', 'B2B', 'Analytics', 'Open Source'],
  enrichedDate: 'Feb 28, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '2,100,000',
      delta: '+12.4%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '22,000',
      delta: '+4,800',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '38,000',
      delta: '+6,200',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '50-80',
      delta: '+8',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'James Hawkins',
  founders: ['James Hawkins', 'Tim Glaser'],
  foundedYear: 2020,
  headcount: '50-80',
  totalRaised: 27_000_000,
  annualRevenue: 25_000_000,
  revenuePerEmployee: 384_000,
  hq: 'San Francisco, CA, USA',
  sector: ['Analytics', 'Open Source'],
  industry: ['Software', 'Developer Tools'],
  keywords: ['Product Analytics', 'Open Source', 'Feature Flags', 'Venture Capital'],
  universe: ['SaaS', 'DevTools'],
  linkedinUrl: 'https://linkedin.com/company/posthog',
  twitterUrl: 'https://twitter.com/PostHog',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/posthog',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'PostHog is a Series B open-source analytics company that has raised $27M, with its latest funding in Oct 2021.',
    'The company reports approximately $25M in ARR with strong product-led growth driven by its open-source community.',
    'Despite a small team of 50-80, hiring is accelerating at 14.2% over the past 6 months, focused on engineering roles.',
    'With approximately $384K in revenue per employee, PostHog demonstrates exceptional efficiency as an all-remote, open-source-first company.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 5, growthPct: 0 },
    { stage: 'Series A', headcount: 25, growthPct: 80 },
    { stage: 'Series B', headcount: 65, growthPct: 55 },
  ],

  fundingRounds: [
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Oct 27, 2021',
      raised: 15_000_000,
      postMoney: null,
      leadInvestors: ['Y Combinator'],
      allInvestors: ['Y Combinator', 'GV', '1984 Ventures', '+3'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'Feb 23, 2021',
      raised: 9_000_000,
      postMoney: null,
      leadInvestors: ['Y Combinator'],
      allInvestors: ['Y Combinator', 'GV', '+2'],
    },
    {
      stage: 'Seed',
      round: 'Seed',
      date: 'Mar 1, 2020',
      raised: 3_000_000,
      postMoney: null,
      leadInvestors: ['Y Combinator'],
      allInvestors: ['Y Combinator', 'Unusual Ventures', '+1'],
    },
  ],

  highlights: [
    '15K+ GitHub stars on main repository',
    'All-remote team across 15+ countries — no offices',
    'Ships weekly with transparent public roadmap and handbook',
    'Self-serve PLG driving 80% of new revenue',
  ],

  legalName: 'PostHog, Inc.',
  alsoKnownAs: [],
  secondaryHq: null,
  companyType: null,
  acquiredBy: 'Y Combinator',

  keyPeople: [
    {
      id: 'james-hawkins',
      name: 'James Hawkins',
      title: 'Co-Founder & CEO',
      location: 'Cambridge, UK',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/j-hawkins',
      twitterUrl: 'https://twitter.com/james406',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Arachnys', logoUrl: null, title: 'VP Engineering', duration: '2y 4m' }],
    },
  ],

  founderPeople: [
    {
      id: 'james-hawkins-f',
      name: 'James Hawkins',
      title: 'Co-Founder & CEO',
      location: 'Cambridge, UK',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/j-hawkins',
      twitterUrl: 'https://twitter.com/james406',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Arachnys', logoUrl: null, title: 'VP Engineering', duration: '2y 4m' }],
    },
    {
      id: 'tim-glaser-f',
      name: 'Tim Glaser',
      title: 'Co-Founder & CTO',
      location: 'London, UK',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/timgl',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Arachnys', logoUrl: null, title: 'Lead Engineer', duration: '2y' }],
    },
  ],

  email: 'hey@posthog.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Oct 27, 2021',
      raised: 15_000_000,
      postMoney: null,
      leadInvestors: ['Y Combinator'],
      allInvestors: ['Y Combinator', '+3'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'Feb 23, 2021',
      raised: 9_000_000,
      postMoney: null,
      leadInvestors: ['Y Combinator'],
      allInvestors: ['Y Combinator', '+2'],
    },
  ],
} as const satisfies ISourcingProfileData;

const LINEAR_PROFILE = {
  id: 'linear',
  name: 'Linear',
  logoUrl: 'https://logo.clearbit.com/linear.app',
  website: 'linear.app',
  description:
    'Linear is a streamlined issue-tracking and project management tool built for modern software teams, focused on speed and keyboard-first workflows.',
  tags: ['Active', 'Private', 'Startup', 'B2B', 'Project Management', 'DevTools'],
  enrichedDate: 'Mar 1, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '5,800,000',
      delta: '+9.3%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '32,000',
      delta: '+5,100',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '85,000',
      delta: '+12,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '80-120',
      delta: '+12',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Karri Saarinen',
  founders: ['Karri Saarinen', 'Tuomas Artman', 'Jori Lallo'],
  foundedYear: 2019,
  headcount: '80-120',
  totalRaised: 52_000_000,
  annualRevenue: 35_000_000,
  revenuePerEmployee: 350_000,
  hq: 'San Francisco, CA, USA',
  sector: ['Project Management', 'DevTools'],
  industry: ['Software', 'Productivity'],
  keywords: ['Issue Tracking', 'Project Management', 'Developer Experience', 'Venture Capital'],
  universe: ['SaaS', 'DevTools'],
  linkedinUrl: 'https://linkedin.com/company/linear-app',
  twitterUrl: 'https://twitter.com/linearapp',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/linear-2',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Linear is a Series B company that has raised $52M, known for its beautifully crafted project management tool that is rapidly displacing Jira among modern software teams.',
    'The company reports approximately $35M in ARR with strong word-of-mouth growth and 150%+ net dollar retention.',
    'Hiring is accelerating at 12.8% over the past 6 months despite maintaining a deliberately lean team.',
    'With approximately $350K in revenue per employee, Linear demonstrates exceptional capital efficiency with a product-led growth model.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 5, growthPct: 0 },
    { stage: 'Series A', headcount: 30, growthPct: 70 },
    { stage: 'Series B', headcount: 100, growthPct: 45 },
  ],

  fundingRounds: [
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Sep 8, 2022',
      raised: 35_000_000,
      postMoney: 400_000_000,
      leadInvestors: ['Accel'],
      allInvestors: ['Accel', 'Sequoia', 'Elad Gil', 'Dylan Field', '+3'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'Jun 15, 2021',
      raised: 12_000_000,
      postMoney: 110_000_000,
      leadInvestors: ['Sequoia'],
      allInvestors: ['Sequoia', 'Elad Gil', 'Y Combinator', '+2'],
    },
    {
      stage: 'Seed',
      round: 'Seed',
      date: 'Nov 1, 2019',
      raised: 5_000_000,
      postMoney: 20_000_000,
      leadInvestors: ['Sequoia'],
      allInvestors: ['Sequoia', 'Y Combinator', '+1'],
    },
  ],

  highlights: [
    'Adopted by 10,000+ software teams worldwide',
    'Known for exceptional design quality and performance',
    'Growing 2x+ YoY with minimal marketing spend',
    'AI-powered triage and project creation features',
  ],

  legalName: 'Linear Orbit, Inc.',
  alsoKnownAs: [],
  secondaryHq: null,
  companyType: null,
  acquiredBy: 'Sequoia Capital',

  keyPeople: [
    {
      id: 'karri-saarinen',
      name: 'Karri Saarinen',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/karrisaarinen',
      twitterUrl: 'https://twitter.com/karrisaarinen',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Airbnb',
          logoUrl: 'https://logo.clearbit.com/airbnb.com',
          title: 'Principal Designer',
          duration: '3y',
        },
        {
          company: 'Coinbase',
          logoUrl: 'https://logo.clearbit.com/coinbase.com',
          title: 'Head of Design',
          duration: '1y 6m',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'karri-saarinen-f',
      name: 'Karri Saarinen',
      title: 'Co-Founder & CEO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/karrisaarinen',
      twitterUrl: 'https://twitter.com/karrisaarinen',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Airbnb',
          logoUrl: 'https://logo.clearbit.com/airbnb.com',
          title: 'Principal Designer',
          duration: '3y',
        },
      ],
    },
    {
      id: 'tuomas-artman-f',
      name: 'Tuomas Artman',
      title: 'Co-Founder & CTO',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/tuomasartman',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Uber',
          logoUrl: 'https://logo.clearbit.com/uber.com',
          title: 'Engineering Manager',
          duration: '3y',
        },
      ],
    },
    {
      id: 'jori-lallo-f',
      name: 'Jori Lallo',
      title: 'Co-Founder',
      location: 'San Francisco, CA, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/jorilallo',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Uber',
          logoUrl: 'https://logo.clearbit.com/uber.com',
          title: 'Software Engineer',
          duration: '2y 6m',
        },
      ],
    },
  ],

  email: 'support@linear.app',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Sep 8, 2022',
      raised: 35_000_000,
      postMoney: 400_000_000,
      leadInvestors: ['Accel'],
      allInvestors: ['Accel', '+3'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'Jun 15, 2021',
      raised: 12_000_000,
      postMoney: 110_000_000,
      leadInvestors: ['Sequoia'],
      allInvestors: ['Sequoia', '+2'],
    },
  ],
} as const satisfies ISourcingProfileData;

const MISTRAL_PROFILE = {
  id: 'mistral',
  name: 'Mistral AI',
  logoUrl: 'https://logo.clearbit.com/mistral.ai',
  website: 'mistral.ai',
  description:
    'Mistral AI is a French AI company building open and commercial large language models, focused on efficiency and performance as an alternative to OpenAI and Anthropic.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'AI Research', 'LLM'],
  enrichedDate: 'Mar 10, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '8,900,000',
      delta: '+15.2%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '95,000',
      delta: '+28,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '180,000',
      delta: '+45,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '200-350',
      delta: '+75',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Arthur Mensch',
  founders: ['Arthur Mensch', 'Guillaume Lample', 'Timothee Lacroix'],
  foundedYear: 2023,
  headcount: '200-350',
  totalRaised: 2_200_000_000,
  annualRevenue: 80_000_000,
  revenuePerEmployee: 296_000,
  hq: 'Paris, France',
  sector: ['AI Research', 'LLM'],
  industry: ['Artificial Intelligence', 'Machine Learning'],
  keywords: ['Open-Source AI', 'LLM', 'Generative AI', 'European AI'],
  universe: ['AI Platform'],
  linkedinUrl: 'https://linkedin.com/company/mistral-ai',
  twitterUrl: 'https://twitter.com/MistralAI',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/mistral-ai',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Mistral AI is a Series B company valued at $6.2B, having raised $2.2B in its first 18 months — one of the fastest funded AI startups in Europe.',
    'The company reports approximately $80M in annualized revenue and is rapidly scaling its commercial API offering.',
    'Hiring is accelerating aggressively at 28.5% over the past 6 months, driven by model research and commercial expansion.',
    'With approximately $296K in revenue per employee, Mistral AI is building a European AI champion as an open-weight alternative to US-based frontier labs.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 15, growthPct: 0 },
    { stage: 'Series A', headcount: 80, growthPct: 120 },
    { stage: 'Series B', headcount: 270, growthPct: 85 },
  ],

  fundingRounds: [
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Jun 11, 2024',
      raised: 600_000_000,
      postMoney: 6_200_000_000,
      leadInvestors: ['General Catalyst'],
      allInvestors: ['General Catalyst', 'Lightspeed', 'a16z', 'BNP Paribas', 'Samsung', '+6'],
    },
    {
      stage: 'Series A',
      round: 'Series A',
      date: 'Dec 11, 2023',
      raised: 415_000_000,
      postMoney: 2_000_000_000,
      leadInvestors: ['a16z'],
      allInvestors: ['a16z', 'Lightspeed', 'BNP Paribas', 'Salesforce Ventures', '+5'],
    },
    {
      stage: 'Seed',
      round: 'Seed',
      date: 'Jun 13, 2023',
      raised: 113_000_000,
      postMoney: 260_000_000,
      leadInvestors: ['Lightspeed'],
      allInvestors: ['Lightspeed', 'Motier Ventures', 'Xavier Niel', '+3'],
    },
  ],

  highlights: [
    'Raised $600M Series B — largest European AI round ever',
    'Mistral Large competitive with GPT-4 at lower cost',
    'Open-weight models (Mistral 7B, Mixtral) widely adopted',
    'Partnership with Microsoft for Azure deployment',
  ],

  legalName: 'Mistral AI SAS',
  alsoKnownAs: [],
  secondaryHq: null,
  companyType: null,
  acquiredBy: 'a16z',

  keyPeople: [
    {
      id: 'arthur-mensch',
      name: 'Arthur Mensch',
      title: 'Co-Founder & CEO',
      location: 'Paris, France',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/arthurmensch',
      twitterUrl: 'https://twitter.com/arthurmensch',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google DeepMind',
          logoUrl: 'https://logo.clearbit.com/deepmind.com',
          title: 'Research Scientist',
          duration: '3y',
        },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'arthur-mensch-f',
      name: 'Arthur Mensch',
      title: 'Co-Founder & CEO',
      location: 'Paris, France',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/arthurmensch',
      twitterUrl: 'https://twitter.com/arthurmensch',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google DeepMind',
          logoUrl: 'https://logo.clearbit.com/deepmind.com',
          title: 'Research Scientist',
          duration: '3y',
        },
      ],
    },
    {
      id: 'guillaume-lample-f',
      name: 'Guillaume Lample',
      title: 'Co-Founder & Chief Scientist',
      location: 'Paris, France',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/guillaumelample',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Meta AI',
          logoUrl: 'https://logo.clearbit.com/meta.com',
          title: 'Research Scientist',
          duration: '5y',
        },
      ],
    },
    {
      id: 'timothee-lacroix-f',
      name: 'Timothee Lacroix',
      title: 'Co-Founder & CTO',
      location: 'Paris, France',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/timothee-lacroix',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Meta AI',
          logoUrl: 'https://logo.clearbit.com/meta.com',
          title: 'Research Engineer',
          duration: '5y',
        },
      ],
    },
  ],

  email: 'contact@mistral.ai',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Jun 11, 2024',
      raised: 600_000_000,
      postMoney: 6_200_000_000,
      leadInvestors: ['General Catalyst'],
      allInvestors: ['General Catalyst', '+6'],
    },
    {
      stage: 'Seed',
      round: 'Seed',
      date: 'Jun 13, 2023',
      raised: 113_000_000,
      postMoney: 260_000_000,
      leadInvestors: ['Lightspeed'],
      allInvestors: ['Lightspeed', '+3'],
    },
  ],
} as const satisfies ISourcingProfileData;

const COHERE_PROFILE = {
  id: 'cohere',
  name: 'Cohere',
  logoUrl: 'https://logo.clearbit.com/cohere.com',
  website: 'cohere.com',
  description:
    'Cohere is an enterprise AI company providing large language models and NLP APIs that help businesses integrate AI into their products and workflows.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'Enterprise AI', 'NLP'],
  enrichedDate: 'Mar 8, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '3,200,000',
      delta: '+8.6%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '72,000',
      delta: '+12,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '58,000',
      delta: '+9,500',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '300-450',
      delta: '+55',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Aidan Gomez',
  founders: ['Aidan Gomez', 'Ivan Zhang', 'Nick Frosst'],
  foundedYear: 2019,
  headcount: '300-450',
  totalRaised: 970_000_000,
  annualRevenue: 60_000_000,
  revenuePerEmployee: 160_000,
  hq: 'Toronto, Canada',
  sector: ['Enterprise AI', 'NLP'],
  industry: ['Artificial Intelligence', 'Machine Learning'],
  keywords: ['Enterprise AI', 'RAG', 'Embeddings', 'Venture Capital'],
  universe: ['AI Platform'],
  linkedinUrl: 'https://linkedin.com/company/cohere-ai',
  twitterUrl: 'https://twitter.com/coaborhere',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/cohere-2',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Cohere is a Series D company valued at $5.5B, having raised $970M to build enterprise-focused large language models.',
    'The company reports approximately $60M in annualized revenue, with a focus on retrieval-augmented generation (RAG) and enterprise deployments.',
    'Hiring is accelerating at 15.2% over the past 6 months as the company expands its go-to-market and research teams.',
    'With approximately $160K in revenue per employee, Cohere is investing heavily in R&D while building out its enterprise sales motion in the competitive LLM market.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 10, growthPct: 0 },
    { stage: 'Series A', headcount: 50, growthPct: 80 },
    { stage: 'Series B', headcount: 150, growthPct: 90 },
    { stage: 'Series C', headcount: 350, growthPct: 50 },
  ],

  fundingRounds: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Jul 22, 2024',
      raised: 500_000_000,
      postMoney: 5_500_000_000,
      leadInvestors: ['PSP Investments'],
      allInvestors: ['PSP Investments', 'Nvidia', 'Salesforce Ventures', 'Cisco', 'AMD', '+5'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Jun 8, 2023',
      raised: 270_000_000,
      postMoney: 2_200_000_000,
      leadInvestors: ['Inovia Capital'],
      allInvestors: ['Inovia Capital', 'Nvidia', 'Oracle', 'Salesforce Ventures', '+4'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Feb 2, 2022',
      raised: 125_000_000,
      postMoney: 600_000_000,
      leadInvestors: ['Tiger Global'],
      allInvestors: ['Tiger Global', 'Index Ventures', 'Radical Ventures', '+3'],
    },
  ],

  highlights: [
    'Co-founded by first author of the "Attention Is All You Need" paper',
    'Command R+ model competitive with GPT-4 for enterprise use cases',
    'Strong focus on on-premise and private cloud deployments',
    'Partnerships with Oracle, AWS, and Google Cloud',
  ],

  legalName: 'Cohere Inc.',
  alsoKnownAs: [],
  secondaryHq: 'San Francisco, CA, USA',
  companyType: null,
  acquiredBy: 'Nvidia',

  keyPeople: [
    {
      id: 'aidan-gomez',
      name: 'Aidan Gomez',
      title: 'Co-Founder & CEO',
      location: 'Toronto, Canada',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/aidangomez',
      twitterUrl: 'https://twitter.com/aidangomez',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google Brain',
          logoUrl: 'https://logo.clearbit.com/google.com',
          title: 'Research Intern',
          duration: '6m',
        },
        { company: 'FOR.ai', logoUrl: null, title: 'Co-Founder', duration: '2y' },
      ],
    },
  ],

  founderPeople: [
    {
      id: 'aidan-gomez-f',
      name: 'Aidan Gomez',
      title: 'Co-Founder & CEO',
      location: 'Toronto, Canada',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/aidangomez',
      twitterUrl: 'https://twitter.com/aidangomez',
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google Brain',
          logoUrl: 'https://logo.clearbit.com/google.com',
          title: 'Research Intern',
          duration: '6m',
        },
      ],
    },
    {
      id: 'ivan-zhang-f',
      name: 'Ivan Zhang',
      title: 'Co-Founder',
      location: 'Toronto, Canada',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/ivanzhang',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google Brain',
          logoUrl: 'https://logo.clearbit.com/google.com',
          title: 'Software Engineer',
          duration: '1y',
        },
      ],
    },
    {
      id: 'nick-frosst-f',
      name: 'Nick Frosst',
      title: 'Co-Founder',
      location: 'Toronto, Canada',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/nickfrosst',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [
        {
          company: 'Google Brain',
          logoUrl: 'https://logo.clearbit.com/google.com',
          title: 'Research Scientist',
          duration: '2y',
        },
      ],
    },
  ],

  email: 'support@cohere.com',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Jul 22, 2024',
      raised: 500_000_000,
      postMoney: 5_500_000_000,
      leadInvestors: ['PSP Investments'],
      allInvestors: ['PSP Investments', '+5'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'Jun 8, 2023',
      raised: 270_000_000,
      postMoney: 2_200_000_000,
      leadInvestors: ['Inovia Capital'],
      allInvestors: ['Inovia Capital', '+4'],
    },
  ],
} as const satisfies ISourcingProfileData;

const HUGGINGFACE_PROFILE = {
  id: 'huggingface',
  name: 'Hugging Face',
  logoUrl: 'https://logo.clearbit.com/huggingface.co',
  website: 'huggingface.co',
  description:
    'Hugging Face is the AI community platform and hub for machine learning, hosting models, datasets, and spaces for collaborative AI development.',
  tags: ['Active', 'Private', 'Growth', 'B2B', 'B2C', 'AI/ML Platform', 'Open Source'],
  enrichedDate: 'Mar 12, 2026',

  trafficStats: [
    {
      label: 'Web Visitors Traffic',
      value: '35,600,000',
      delta: '+11.8%',
      deltaPositive: true,
      deltaDescription: 'MoM',
    },
    {
      label: 'LinkedIn Followers',
      value: '240,000',
      delta: '+35,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'X Followers',
      value: '320,000',
      delta: '+42,000',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
    {
      label: 'Headcount',
      value: '200-300',
      delta: '+40',
      deltaPositive: true,
      deltaDescription: 'in last 6 months',
    },
  ],
  operationalStatus: 'Operating',

  ceoName: 'Clement Delangue',
  founders: ['Clement Delangue', 'Julien Chaumond', 'Thomas Wolf'],
  foundedYear: 2016,
  headcount: '200-300',
  totalRaised: 395_000_000,
  annualRevenue: 70_000_000,
  revenuePerEmployee: 280_000,
  hq: 'New York, NY, USA',
  sector: ['AI/ML Platform', 'Open Source'],
  industry: ['Artificial Intelligence', 'Machine Learning'],
  keywords: ['Transformers', 'Model Hub', 'Open Source', 'ML Community'],
  universe: ['AI Platform', 'Developer Platform'],
  linkedinUrl: 'https://linkedin.com/company/huggingface',
  twitterUrl: 'https://twitter.com/huggingface',
  facebookUrl: null,
  crunchbaseUrl: 'https://crunchbase.com/organization/hugging-face',
  pitchbookUrl: null,
  harmonicUrl: null,

  foresightSummary: [
    'Hugging Face is a Series D company valued at $4.5B, having raised $395M as the central hub for the open-source AI/ML community.',
    'The company reports approximately $70M in ARR from its enterprise hub, inference API, and spaces platform.',
    'Hiring is accelerating at 18.5% over the past 6 months, driven by demand for managed ML infrastructure.',
    'With approximately $280K in revenue per employee, Hugging Face is monetizing its massive community (1M+ users, 500K+ models) while remaining the default platform for ML collaboration.',
  ],

  fundingEvolution: [
    { stage: 'Seed', headcount: 8, growthPct: 0 },
    { stage: 'Series A', headcount: 30, growthPct: 65 },
    { stage: 'Series B', headcount: 80, growthPct: 85 },
    { stage: 'Series C', headcount: 170, growthPct: 60 },
    { stage: 'Series D', headcount: 250, growthPct: 35 },
  ],

  fundingRounds: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Aug 24, 2023',
      raised: 235_000_000,
      postMoney: 4_500_000_000,
      leadInvestors: ['Google', 'Amazon', 'Nvidia'],
      allInvestors: ['Google', 'Amazon', 'Nvidia', 'Intel', 'AMD', 'Salesforce Ventures', '+4'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'May 9, 2022',
      raised: 100_000_000,
      postMoney: 2_000_000_000,
      leadInvestors: ['Lux Capital', 'Sequoia'],
      allInvestors: ['Lux Capital', 'Sequoia', 'Coatue', 'Addition', '+3'],
    },
    {
      stage: 'Series B',
      round: 'Series B',
      date: 'Mar 11, 2021',
      raised: 40_000_000,
      postMoney: 500_000_000,
      leadInvestors: ['Addition'],
      allInvestors: ['Addition', 'Lux Capital', 'a16z', 'Betaworks', '+2'],
    },
  ],

  highlights: [
    'Hosts 500K+ ML models and 100K+ datasets',
    'Strategic investor group includes Google, Amazon, Nvidia, AMD, Intel',
    'Transformers library is the standard for ML research',
    'Community of 1M+ developers and researchers',
  ],

  legalName: 'Hugging Face, Inc.',
  alsoKnownAs: ['HF'],
  secondaryHq: 'Paris, France',
  companyType: null,
  acquiredBy: 'Google',

  keyPeople: [
    {
      id: 'clement-delangue',
      name: 'Clement Delangue',
      title: 'Co-Founder & CEO',
      location: 'New York, NY, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/clementdelangue',
      twitterUrl: 'https://twitter.com/ClementDelangue',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Waynaut', logoUrl: null, title: 'CEO & Co-Founder', duration: '2y' }],
    },
  ],

  founderPeople: [
    {
      id: 'clement-delangue-f',
      name: 'Clement Delangue',
      title: 'Co-Founder & CEO',
      location: 'New York, NY, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/clementdelangue',
      twitterUrl: 'https://twitter.com/ClementDelangue',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Waynaut', logoUrl: null, title: 'CEO & Co-Founder', duration: '2y' }],
    },
    {
      id: 'julien-chaumond-f',
      name: 'Julien Chaumond',
      title: 'Co-Founder & CTO',
      location: 'New York, NY, USA',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/julienchaumond',
      twitterUrl: null,
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Stupeflix', logoUrl: null, title: 'CTO', duration: '3y' }],
    },
    {
      id: 'thomas-wolf-f',
      name: 'Thomas Wolf',
      title: 'Co-Founder & CSO',
      location: 'Paris, France',
      avatarUrl: null,
      linkedinUrl: 'https://linkedin.com/in/thomwolf',
      twitterUrl: 'https://twitter.com/Thom_Wolf',
      facebookUrl: null,
      recentWorkHistory: [{ company: 'Snips', logoUrl: null, title: 'VP Science', duration: '3y 6m' }],
    },
  ],

  email: 'team@huggingface.co',
  phone: null,

  similarCompanies: [
    {
      stage: 'Series D',
      round: 'Series D',
      date: 'Aug 24, 2023',
      raised: 235_000_000,
      postMoney: 4_500_000_000,
      leadInvestors: ['Google'],
      allInvestors: ['Google', '+4'],
    },
    {
      stage: 'Series C',
      round: 'Series C',
      date: 'May 9, 2022',
      raised: 100_000_000,
      postMoney: 2_000_000_000,
      leadInvestors: ['Lux Capital'],
      allInvestors: ['Lux Capital', '+3'],
    },
  ],
} as const satisfies ISourcingProfileData;

export const SOURCING_PROFILES_MAP: ReadonlyMap<string, ISourcingProfileData> = new Map([
  ['anthropic', SOURCING_PROFILE_MOCK],
  ['openai', OPENAI_PROFILE],
  ['stripe', STRIPE_PROFILE],
  ['databricks', DATABRICKS_PROFILE],
  ['figma', FIGMA_PROFILE],
  ['notion', NOTION_PROFILE],
  ['vercel', VERCEL_PROFILE],
  ['scaleai', SCALEAI_PROFILE],
  ['anduril', ANDURIL_PROFILE],
  ['rippling', RIPPLING_PROFILE],
  ['ramp', RAMP_PROFILE],
  ['brex', BREX_PROFILE],
  ['plaid', PLAID_PROFILE],
  ['airtable', AIRTABLE_PROFILE],
  ['retool', RETOOL_PROFILE],
  ['posthog', POSTHOG_PROFILE],
  ['linear', LINEAR_PROFILE],
  ['mistral', MISTRAL_PROFILE],
  ['cohere', COHERE_PROFILE],
  ['huggingface', HUGGINGFACE_PROFILE],
]);
